import React, { useState, useEffect, useCallback, useRef, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { docText, refusalCode, refusalMessage, useDocLang } from './docText';
import { s4aDone, s4bDone } from './section4Steps';
import { isTaskOverdue, sortPendingTasks, OverdueLabel, OVERDUE_RED } from './taskOverdue';
import { useDarkMode } from './DarkModeContext';
import { useConfirm, LoadError, CareerTimeline, ScrollableTable, useIsNarrow, gridCols,
         ArchiveReplaceModal, REPLACEMENT_REQUIRED_TYPES, Labeled } from './ui';
import axios from 'axios';
import { previewBlob, saveBlob } from './downloadFile';
import { useToast } from './ToastContext';
import SponsorForm from './SponsorForm';
import LeaverForm from './LeaverForm';
import NewStarterForm from './NewStarterForm';
import DocumentUpload from './DocumentUpload';
import { DOC_CATEGORIES, DATE_EXPIRY_TYPES, documentTypeLabel } from './MigrationConfirmation';
import { ENTITIES } from './PolicyRepository';
import { GOLD, NAVY_FORM, NAVY_SHELL, TEXT_DARK_MODE, TEXT_MUTED } from './colors';
import { UPLOAD_TASK_TO_DOC_TYPE } from './EmployeeView';
import { isInternshipEnded } from './internship';
// DATEDAY: date-only values shown as their calendar day in every timezone.
import { toDisplayDate } from './dateOnly';

const API = '/api';

// CARDHEAD: the ideal width of the header name block. The block holds the name, job title, entity,
// login email and the visa/CEO badges; below roughly this width those lines start breaking
// mid-phrase, which is the "one word per line" column the fix exists to prevent. Used as the
// flexBasis of the name block, so the action buttons wrap to their own line once they can no
// longer sit beside a block this wide. Not a breakpoint: the wrap is decided by the available
// space in the card, so it works inside any shell regardless of sidebar width.
const NAME_MIN_BASIS = 320;

// TASKDEEPLINK: task_types that SHOULD scroll/highlight a specific card when a COO clicks the task
// from the Dashboard. The card must have a ref registered in cardRefs (below) for the scroll to
// fire - it is a safe no-op otherwise. Scoping decision (point 3): the Background Check card is the
// one COO-facing card that is universal (not visa-gated) and has both an upload path and a
// review path, so it is the deep-link target for both upload_background_check (which ALSO opens
// the upload modal) and the non-upload review_background_check (scroll+highlight is the whole
// action, since there is no modal). Other upload task_types (upload_right_to_work,
// upload_job_advertisement, etc.) deep-link by OPENING THE UPLOAD MODAL via UPLOAD_TASK_TO_DOC_TYPE
// - the modal IS the action, so no separate scroll target is needed for them; the Right to Work
// controls also live inside a visa-only checklist card, which would be an unreliable scroll anchor.
const TASK_TYPE_TO_CARD = {
  review_background_check:  'background_check',
  upload_background_check:  'background_check',  // also opens the upload modal (upload mapping)
  // RTWDEEPLINKFIX: the "Complete Right to Work verification" task (upload_right_to_work) no longer
  // opens the generic upload modal (removed from UPLOAD_TASK_TO_DOC_TYPE - that was a dead end since
  // RTWTASKAUTO completes the task via the two dedicated RTW sub-sections, not a generic
  // document_type='right_to_work' upload). It now scroll-highlights the dedicated Right to Work
  // section AND expands it (see the deep-link effect), landing the COO on the checklist +
  // Check Conducted sub-tabs where the work actually happens. No upload modal opens.
  upload_right_to_work:     'right_to_work',
};

// TASKDEEPLINK: transient ring drawn around a card the deep-link scrolled to, so the COO's eye
// lands on the exact place to act. Cleared after ~2.6s by the effect that sets it.
// HRPROFILE follow-up: HR's own upload tasks. Opened from a task list, the bell, the recap email or
// this profile's Tasks card, each lands on its upload dialog with the type selected; the background
// check result goes to Section 4B for a new joiner (S4AB).
const HR_UPLOAD_TASK_TYPES = new Set(['upload_cv', 'upload_contract', 'upload_job_advertisement',
                                      'upload_background_check_result']);

const CARD_HIGHLIGHT = {
  boxShadow: `0 0 0 3px ${GOLD}`,
  transition: 'box-shadow 0.3s ease',
};

// Same canonical entity value set as primary_entity (PolicyRepository.js's ENTITIES / backend's
// COMPANY_TO_ENTITY values), minus the "All Entities" filter-only option.
const SECONDARY_ENTITY_OPTIONS = ENTITIES.filter(e => e !== 'All Entities');

// Granular right-to-work verification items (key → label), filled by the COO
const RTW_FIELDS = [
  ['share_code_obtained',          'Share code obtained (biometric residence cards no longer accepted)'],
  ['original_documents_verified',  'Original documents verified in person'],
  ['copies_kept',                  'Copies of documents kept'],
  ['photos_match',                 'Photos match across all documents'],
  ['dates_of_birth_match',         'Dates of birth match across all documents'],
  ['right_to_work_not_expired',    'Right to work dates not expired'],
  ['permission_type_confirmed',    'Permission for type of work confirmed'],
  ['home_office_letter_obtained',  'Home Office decision letter obtained'],
];

// Confirmed final criteria from Compliance for which employees see the second, non-visa Right to
// Work verification path - gov.uk Employer Checking Service - instead of the gov.uk share-code
// "Proof of Right to Work Check Conducted" path above: no visa sponsorship required, UK-resident,
// AND not a British national - this path covers e.g. pre-Brexit settled-status residents and EU
// nationals with settled status, who still need a one-time manual check; British nationals need
// neither RTW path (see the informational note below instead). isUkResidentCountry/isBritishNational
// are split out (not inlined) so the "gap" note condition below can reuse the exact same
// comparisons rather than duplicating the string-matching logic a second time.
// ARCHIVEGATE: mirrors the backend's MANUAL_ARCHIVE_TYPES (documents.py) - the only types a
// non-coo/admin account may manually archive, and then only on its OWN record. Keep in sync if
// that set changes. EmployeeView.js carries the identical mirror for the same reason.
// SELFARCHCONTRACT: contract and cv added, matching the backend set. A REVIEWED document is
// refused server-side whatever its type, so the button may still 403 - handleArchiveDocument
// already surfaces the server's own reason (ARCHIVEERR), which is the readable explanation.
const MANUAL_ARCHIVE_TYPES = new Set(['passport', 'visa', 'proof_of_address', 'p45', 'contract', 'cv']);

const isUkResidentCountry = (employeeRaw) => {
  const country = (employeeRaw?.country_of_residence || '').trim().toLowerCase();
  return country === 'united kingdom' || country === 'uk';
};

const isBritishNational = (employeeRaw) => {
  const nationality = (employeeRaw?.nationality || '').trim().toLowerCase();
  return nationality === 'british' || nationality === 'uk' || nationality === 'united kingdom';
};

const isEligibleForEmployerCheckingServiceCheck = (employeeRaw, requiresVisa) => {
  return !requiresVisa && isUkResidentCountry(employeeRaw) && !isBritishNational(employeeRaw);
};

// MGRPROFILE: the server's refusals of PATCH /employees/{id}/manager, shown translated.
const MANAGER_ERROR_KEYS = {
  'An employee cannot be their own manager': 'mgrProfile.errSelf',
  'The manager must be an active employee': 'mgrProfile.errInactive',
  'Manager not found': 'mgrProfile.errInactive',
};

function EmployeeProfile({ employeeId, taskDeepLinkType = null, currentUser, role, onBack, onNavigateToNewStarters = null, onNavigateToEmployees = null, onFormOpenChange = null }) {
  // TASKACTOR: the only i18n consumer in this file so far. Named dashT to match the convention
  // Dashboard.js uses, since several callbacks here already bind `t` as a row argument.
  const { t: dashT } = useTranslation();
  // DOCREFUSAL: document messages in the viewer's language, and the server's real reason.
  const docLang = useDocLang();
  const { isDarkMode } = useDarkMode();
  const p = mkP(isDarkMode);
  // A11YLABEL: one id prefix per component INSTANCE, so two copies of this screen on one page
  // cannot produce duplicate ids. Declared at the top level of the component on purpose: the
  // labels that use it sit inside nested render helpers and inline arrow components, which are
  // not where a hook may live.
  const a11yId = useId();
  // CLPROFILE (confirmed COO decision, SUPERSEDES PAYPROFILEVIEW's trim): a payroll+delegate
  // account is a COUNTRY LEAD - the person who runs the entity in their country - so for employees
  // of their OWN ENTITY they now see the SAME profile the COO sees, including Documents, the
  // Signature/Photo card and every visa/Right-to-Work/sponsor section, and they may upload
  // documents.
  //
  // TWO THINGS DELIBERATELY WITHHELD, and they are the whole point of keeping this flag:
  //   1. "Fill IT Setup" - Section 2 belongs to the COO. Suppressed by clearing s2's fillSection
  //      below, so the STATUS row still shows (they need to know whether IT Setup is done) while
  //      the action does not render.
  //   2. Archiving a document - see canArchiveDocument, which still excludes payroll. Uploading
  //      and archiving are different powers; only uploading was granted.
  // The Compliance DECLARATIONS module (gifts, conflicts, PAD, public officials, attestations) is
  // a separate module this profile never renders, and its endpoints refuse a payroll account for
  // any employee but itself - unchanged and out of scope here.
  //
  // role==='payroll' remains a sufficient signal because a payroll account has no OTHER path into
  // EmployeeProfile. The BACKEND is the real access control: every documents endpoint re-checks
  // the entity scope against the actual employee row, so this flag only decides what is OFFERED.
  const isPayrollDelegateView = role === 'payroll';
  // CLMANAGER: may THIS viewer change the line manager? The COO anywhere, plus a Country Lead
  // (the payroll shell) for an employee the backend already admitted them to - reaching this
  // profile at all means they passed payroll_delegate_may_view_profile, so the entity scope is
  // already established by the time this renders.
  //
  // MGRNOADMIN (confirmed decision by Marie-Lynne): ADMIN IS DELIBERATELY EXCLUDED, and this is
  // the one place the gate intentionally does NOT mirror the server. _authorize_correction still
  // admits coo AND admin, and is deliberately left alone - see item 2 of this task. The backend
  // is the security boundary and is unchanged; this flag only decides what is OFFERED, and
  // offering admin a control nobody asked for is a product decision, not a permission one.
  // Narrowing here can only ever hide a control from someone the server would have accepted,
  // which is safe; it can never grant one.
  //
  // Admin reached this control only as a side effect of MGRPICKERFIX moving the block out of the
  // surrounding `role === 'coo'` fragment - before that the outer check refused admin first, so
  // admin never actually had it in practice. This restores that observable behaviour rather than
  // taking anything away that was in use.
  //
  // CLPRIVTARGET: canEditManager's isPrivilegedTarget arm is defined further below, right after
  // employeeRaw itself is declared (useState) - it reads employeeRaw.account_role, so it cannot
  // be computed here where employeeRaw does not exist yet. See that definition for the full
  // rationale; canEditManager is still declared once, only later in this function.
  // CLPROFILE: mirrors _COUNTRY_LEAD_UPLOAD_TYPES in backend/app/routers/documents.py, which is the
  // enforcing copy. Kept in the same order so the two can be diffed by eye. background_check
  // (Compliance) and right_to_work_check_conducted (a compliance artefact) are deliberately absent,
  // as are photo/signature (written by the self-service path and auto-archived) and the UK sponsor
  // licence evidence types (COO owned).
  const COUNTRY_LEAD_UPLOAD_TYPES = [
    'passport', 'visa', 'visa_stamp', 'right_to_work', 'proof_of_address',
    'cv', 'contract', 'p45', 'rib', 'payroll_application', 'pension',
    'health_insurance', 'other',
  ];
  // RESPONSIVE Phase 2: the Details grid (repeat(3,1fr)) + the two-col row reflow below ~900px.
  const isNarrow = useIsNarrow();
  const showToast = useToast();
  const [showRecruitment, setShowRecruitment] = useState(false);
  // TASKHIDEDONE: completed tasks are hidden from the Tasks card by default (Craig: "I see no
  // benefit to having them listed"); this toggle reveals them for audit. Display-only - no
  // Task row is ever deleted or archived in the database.
  const [showCompletedTasks, setShowCompletedTasks] = useState(false);
  // CONFIRMMODAL: styled confirmation replacing this file's 3 native window.confirm calls.
  const [askConfirm, confirmModal] = useConfirm(isDarkMode);
  // LOADERR: which of this profile's loads FAILED (network/server error, never a 404 - a 404 on
  // the form/RTW endpoints is the normal "no record" answer). A failed load must never render as
  // "Employee not found", "No documents" or an unstarted process.
  const [loadErrors, setLoadErrors] = useState([]);
  const addLoadError = (key) => setLoadErrors(prev => (prev.includes(key) ? prev : [...prev, key]));
  const [leaverSection, setLeaverSection] = useState(null);
  const [showNsSection, setShowNsSection] = useState(null);
  const [data, setData] = useState(null);
  const [newStarterForm, setNewStarterForm] = useState(null);
  const [leaverForm, setLeaverForm] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [docLoading, setDocLoading] = useState(null);
  // ARCHREPLACE: the document whose archive-and-replace dialog is open (null = closed).
  const [replaceDoc, setReplaceDoc] = useState(null);
  const [isArchived, setIsArchived] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [employeeRaw, setEmployeeRaw] = useState(null);
  // CLPRIVTARGET: _authorize_correction now refuses a Country Lead on a TARGET whose linked
  // account holds any role other than "employee" (see backend/app/routers/employees.py). That
  // refusal is the real control and is enforced server side regardless of what renders here - see
  // item 6 of this task. This flag only avoids OFFERING a control that a coo/compliance/hr/etc
  // target would now make fail with a 403. employeeRaw.account_role is exactly what
  // _authorize_correction itself checks (get_employee sets it from the SAME UserAccount row,
  // account.role), so this can never disagree with the server's refusal. No linked account
  // (account_role is null) is the ordinary not-yet-provisioned case and must keep working - see
  // item 3 - so only an EXPLICIT non-employee role hides the control, never a missing one.
  const isPrivilegedTarget = !!employeeRaw?.account_role && employeeRaw.account_role !== 'employee';
  // HRPROFILE (2026-10-27, business decision): hr sets and changes the line manager as the COO does.
  // MGRPROFILE (2026-10-28, business decision): the line manager is changed or removed only here, so
  // the COO, admin and HR all have the field (admin was left out by MGRNOADMIN, superseded), with the
  // current manager shown, a Remove manager action and a refusal shown translated beside the field.
  // The Country Lead keeps the field exactly as before (managerFieldFull false).
  const managerFieldFull = role === 'coo' || role === 'admin' || role === 'hr';
  const canEditManager = managerFieldFull || (isPayrollDelegateView && !isPrivilegedTarget);
  // CLPROFILEFIELDS: the name / job title / company email correction controls all call
  // _authorize_correction server side (update_name, update_job_title, update_company_email),
  // which admits coo, admin, OR a Country Lead (payroll_delegate_may_view_profile) refused only
  // when CLPRIVTARGET's target-role deny-list fires. Unlike canEditManager (MGRNOADMIN
  // deliberately excludes admin as a product decision), these three mirror the server's actual
  // admit set exactly, since admin's existing unconditional visibility here must not change.
  // Reuses isPrivilegedTarget/isPayrollDelegateView exactly as canEditManager does - no new flag,
  // no new condition shape. Out-of-entity scoping needs no extra check here: a Country Lead never
  // reaches this profile for an employee outside their entity at all (payroll_delegate_may_view_profile
  // 403s the GET itself), so this can only ever evaluate for an employee already in scope.
  // HRPROFILE: hr edits name, title and login email as the COO does (the server refuses hr the login
  // email of a COO or admin account, HRLOGINGUARD, shown below as for the archive).
  const canEditProfileFields = role === 'coo' || role === 'admin' || role === 'hr' || (isPayrollDelegateView && !isPrivilegedTarget);
  // HRPROFILE: the COO's profile settings (the switches and the saved fields under Details), and the
  // team. hr has them all except the internship end date, which the brief does not list.
  const hasCooSettings = role === 'coo' || role === 'hr';
  const canEditTeam = role === 'coo' || role === 'hr';
  const [managerName, setManagerName] = useState(null);
  // HRPROFILE: the team (Employee.function_team), its suggestions and its save.
  const [team, setTeam] = useState('');
  const [teamOptions, setTeamOptions] = useState([]);
  const [savingTeam, setSavingTeam] = useState(false);
  const [canSubmitNewStarters, setCanSubmitNewStarters] = useState(false);
  const [togglingPermission, setTogglingPermission] = useState(false);
  // P45COO: in-flight guard for the COO-side "Don't have P45" waiver control (see the openTasks
  // task list). Separate from EmployeeView's own NoP45Link/P45DECL flow, which is untouched.
  const [p45Saving, setP45Saving] = useState(false);
  const [requiresVisa, setRequiresVisa] = useState(false);
  const [togglingVisa, setTogglingVisa] = useState(false);
  const [hasResidencePermit, setHasResidencePermit] = useState(false);
  const [togglingPermit, setTogglingPermit] = useState(false);
  const [isFrequentTraveller, setIsFrequentTraveller] = useState(false);
  const [togglingTraveller, setTogglingTraveller] = useState(false);
  // CONTRACTEXEMPT2: coo/admin-only post-creation toggle for the contract-upload exemption.
  const [contractExempt, setContractExempt] = useState(false);
  const [togglingContractExempt, setTogglingContractExempt] = useState(false);
  const [delegateEmail, setDelegateEmail] = useState('');
  // NAMEEDIT: coo/admin-editable first/last name correction, display-only (does not touch
  // company_email/login identity). editingName toggles the header between read view and edit view.
  const [editingName, setEditingName] = useState(false);
  // EMAILFIX: correcting company_email also moves the LOGIN identity (UserAccount.email), so
  // this is deliberately a separate control from the name edit above, with its own warning and
  // a confirmation step before it fires.
  // CAREERTL: the career timeline plus its two coo/admin controls.
  const [roleHistory, setRoleHistory] = useState([]);
  const [roleHistoryLoading, setRoleHistoryLoading] = useState(true);
  const [roleHistoryError, setRoleHistoryError] = useState(false);
  const [editingJobTitle, setEditingJobTitle] = useState(false);
  const [editJobTitle, setEditJobTitle] = useState('');
  const [savingJobTitle, setSavingJobTitle] = useState(false);
  const [converting, setConverting] = useState(false);
  // CONVERTFORM: the conversion is now a small data-collection form (end date, job title, target
  // type) instead of an instant one-click confirm. Its own open flag, field state and error line.
  const [showConvertForm, setShowConvertForm] = useState(false);
  const [convertForm, setConvertForm] = useState({ internship_end_date: '', job_title: '', employee_type: 'employee' });
  const [convertError, setConvertError] = useState(null);
  const [editingCompanyEmail, setEditingCompanyEmail] = useState(false);
  const [editCompanyEmail, setEditCompanyEmail] = useState('');
  const [savingCompanyEmail, setSavingCompanyEmail] = useState(false);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [savingName, setSavingName] = useState(false);
  // RESENDWELCOME: retroactively settable personal email (welcome-email delivery target).
  const [personalEmail, setPersonalEmail] = useState('');
  const [savingPersonalEmail, setSavingPersonalEmail] = useState(false);
  const [savingDelegate, setSavingDelegate] = useState(false);
  const [secondaryEntity, setSecondaryEntity] = useState('');
  const [savingSecondaryEntity, setSavingSecondaryEntity] = useState(false);
  // INTERNEND: coo/admin-editable real internship end date. Mirrors secondaryEntity's state pair.
  const [internshipEndDate, setInternshipEndDate] = useState('');
  const [savingInternshipEnd, setSavingInternshipEnd] = useState(false);
  const [resendingDelegate, setResendingDelegate] = useState(false);
  // Manager edit control - replicates AddEmployee.js's Line Manager search-by-name field
  // (same state shape, same client-side filter, same dropdown) rather than extracting a shared
  // component: it's small (~50 lines) and this file already follows the inline-edit-widget
  // convention for its delegate_email/secondary_entity siblings, so inlining keeps this feature
  // consistent with the pattern already established on this exact page.
  const [allEmployees, setAllEmployees] = useState([]);
  const [managerSearch, setManagerSearch] = useState('');
  const [managerSearchName, setManagerSearchName] = useState('');
  const [showManagerList, setShowManagerList] = useState(false);
  const [savingManager, setSavingManager] = useState(false);
  // MGRPICKERFIX: true only when the manager list fetch itself failed - distinct from "fetch
  // succeeded but this search matched nobody" (filteredManagers.length === 0), so the field can
  // show a visible, honest message for each rather than a search box that always looks the same
  // whether it is broken or just has no results. Mirrors AddEmployee.js's own MGRSEARCH flag
  // (managerSearchUnavailable), which already draws exactly this distinction for the fetch-failed
  // half; this page previously had no equivalent at all for either half.
  const [managerListUnavailable, setManagerListUnavailable] = useState(false);
  // The edit control's own "currently selected for editing" id - kept separate from the
  // existing read-only managerName/employeeRaw.manager_id (used by the always-visible Manager
  // field above the coo-gated block) so typing/selecting here never disturbs that display, and so
  // this doesn't race the read-only useEffect's own async name lookup.
  const [pendingManagerId, setPendingManagerId] = useState('');
  const [managerError, setManagerError] = useState(null);         // MGRPROFILE: shown beside the field
  const managerFieldRef = useRef(null);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadDocType, setUploadDocType] = useState('');
  const [showSalary, setShowSalary] = useState(false);

  // TASKDEEPLINK: when the COO arrives here from a Dashboard task click, taskDeepLinkType carries
  // the clicked task_type. Once data has loaded, deep-link to the exact action: an upload-type
  // task opens the same upload modal EmployeeView.handleTaskClick opens (reusing the shared
  // UPLOAD_TASK_TO_DOC_TYPE mapping); any other task_type with a target card scrolls it into view
  // and briefly highlights it. cardRefs holds a ref per card key; highlightCard drives the ring.
  const cardRefs = useRef({});
  const [highlightCard, setHighlightCard] = useState(null);
  const deepLinkDone = useRef(false);

  // Tell the parent (Dashboard) when the New Starter Form is open so it can hide its own
  // outer "Employees › {name}" breadcrumb - the form renders its own breadcrumb.
  useEffect(() => {
    onFormOpenChange?.(!!showNsSection);
  }, [showNsSection]); // eslint-disable-line react-hooks/exhaustive-deps

  // HRPROFILE follow-up: a new joiner (neither migrated nor existing, NJCSCOPE) has the background
  // check in Section 4B; anyone else has it in the upload dialog.
  const isNewJoiner = !!employeeRaw && !employeeRaw.is_migrated && !employeeRaw.migration_confirmed;
  const openUploadForTask = (taskType) => {
    if (taskType === 'upload_background_check_result') {
      if (isNewJoiner) { setShowNsSection(4); return; }
      setUploadDocType('background_check');
      setShowUpload(true);
      return;
    }
    const docType = UPLOAD_TASK_TO_DOC_TYPE[taskType];
    if (docType !== undefined) { setUploadDocType(docType); setShowUpload(true); }
  };

  // TASKDEEPLINK: run the deep-link once, after the profile data has loaded (so the target card is
  // actually mounted). No task context (opened via "View" from the employee list) => this is a
  // no-op and the profile behaves exactly as before.
  useEffect(() => {
    if (!taskDeepLinkType || deepLinkDone.current || loading || !data) return;
    deepLinkDone.current = true;
    if (HR_UPLOAD_TASK_TYPES.has(taskDeepLinkType)) {
      openUploadForTask(taskDeepLinkType);
      return;
    }
    const docType = UPLOAD_TASK_TO_DOC_TYPE[taskDeepLinkType];
    if (docType !== undefined) {
      // Upload-type task: open the same upload modal EmployeeView opens for this task_type.
      setUploadDocType(docType);
      setShowUpload(true);
    }
    // Scroll to (and briefly highlight) the relevant card, if this task_type maps to one. For an
    // upload task this also frames the card behind the modal; for a non-upload task (e.g.
    // review_background_check) it is the whole deep-link behaviour.
    const cardKey = TASK_TYPE_TO_CARD[taskDeepLinkType];
    // RTWDEEPLINKFIX: the Right to Work section is collapsed by default; expand it so the COO lands
    // on the checklist + Check Conducted sub-tabs (where the work actually is) rather than a closed
    // header. This is the whole deep-link action for upload_right_to_work - no upload modal opens
    // (it is intentionally absent from UPLOAD_TASK_TO_DOC_TYPE above).
    if (cardKey === 'right_to_work') {
      setRtwExpanded(true);
    }
    if (cardKey && cardRefs.current[cardKey]) {
      cardRefs.current[cardKey].scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightCard(cardKey);
      setTimeout(() => setHighlightCard(null), 2600);
    }
  }, [taskDeepLinkType, loading, data]); // eslint-disable-line react-hooks/exhaustive-deps
  const [showDocList, setShowDocList] = useState(false);
  const [docListType, setDocListType] = useState('');
  const [archivingDoc, setArchivingDoc] = useState(null);
  // CATFIX: inline categorization of a stray 'uncategorized' document by coo/admin/hr on the
  // employee's behalf (same endpoint + category list as EmployeeView / MigrationConfirmation).
  const [categorizeType, setCategorizeType] = useState({});
  const [categorizingDoc, setCategorizingDoc] = useState(null);
  // CATFIX2: the metadata MigrationConfirmation's Step A already requires (expiry date for
  // dated types, passport number + country for passports), previously silently skipped by
  // this inline control - the backend accepted the fields all along.
  const [categorizeMeta, setCategorizeMeta] = useState({});
  const [rtwCheck, setRtwCheck] = useState(null);
  const [rtwForm, setRtwForm] = useState(null);
  const [rtwExpanded, setRtwExpanded] = useState(false);
  const [rtwSaving, setRtwSaving] = useState(false);
  const [rtwSubTab, setRtwSubTab] = useState('document');
  const [rtwConducted, setRtwConducted] = useState(null);
  const [rtwConductedForm, setRtwConductedForm] = useState(null);
  const [rtwConductedSaving, setRtwConductedSaving] = useState(false);
  const niRef = useRef(null);
  const contactRef = useRef(null);
  const scrollToNi = () => niRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  const scrollToContact = () => contactRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  // MGRPICKERFIX: was gated on role === 'coo' only, but canEditManager (the gate on the control
  // that actually RENDERS this search box) also admits isPayrollDelegateView - a Country Lead
  // reached this block, saw a live-looking input, and every keystroke filtered an allEmployees
  // array that could never be populated, because this effect never ran for them. No error, no
  // explanation, just a search that could never return a result.
  //
  // Fixed by calling GET /employees/manager-search rather than widening GET /employees/?status=
  // active to the payroll role: that endpoint is the full staff directory (coo/admin/hr/
  // compliance only, employees.py's get_employees), returns the full record including financial/
  // medical fields, and a Country Lead has no business reading it. manager-search already exists
  // for exactly this shape of gap (MGRSEARCH, built for AddEmployee.js's delegate mount) - id/
  // first_name/last_name only, and its own gate already admits current_user.can_submit_new_starters,
  // which is a STRICT PRECONDITION of even reaching this control for a payroll account
  // (canEditManager's isPayrollDelegateView arm renders only after payroll_delegate_may_view_profile
  // admitted this profile at all, and that predicate itself requires can_submit_new_starters - see
  // employees.py). So every account that can see this control already satisfies manager-search's
  // gate; nothing is widened, and coo/admin (who already passed) are unaffected since manager-search
  // admits them too.
  //
  // The fetch failure is now surfaced rather than swallowed: managerListUnavailable drives a
  // visible message distinct from "no matches for this search" (see the render below).
  useEffect(() => {
    if (!canEditManager) return;
    axios.get(`${API}/employees/manager-search`)
      .then(res => setAllEmployees(res.data))
      .catch(() => setManagerListUnavailable(true));
  }, [canEditManager]); // eslint-disable-line

  // HRPROFILE: the teams already in use, offered as suggestions so one team is not spelt three ways.
  useEffect(() => {
    if (!canEditTeam) return;
    axios.get(`${API}/employees/teams`)
      .then(res => setTeamOptions(Array.isArray(res.data) ? res.data : []))
      .catch(() => setTeamOptions([]));
  }, [canEditTeam]); // eslint-disable-line

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (managerFieldRef.current && !managerFieldRef.current.contains(e.target)) {
        setShowManagerList(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredManagers = managerSearch.trim().length < 1 ? [] : allEmployees.filter(emp => {
    if (emp.id === employeeId) return false; // an employee cannot be their own manager
    const full = `${emp.first_name} ${emp.last_name}`.toLowerCase();
    return full.includes(managerSearch.toLowerCase());
  }).slice(0, 8);

  // CONFIRMMODAL: the three window.confirm calls below became askConfirm - the styled
  // ConfirmModal (COMPMODAL pattern) with the exact same action running on confirm.
  const handleArchiveToggle = () => {
    // HRARCHIVEGUARD: hr may not archive an employee holding the COO or admin role. The button stays
    // visible; clicking it explains why, and the server refuses it too (shown in the catch below).
    const _privilegedMsg = docText('employeeArchive.privilegedByHr', docLang);
    if (role === 'hr' && !isArchived && ['coo', 'admin'].includes(employeeRaw?.account_role)) {
      showToast(_privilegedMsg, 'error');
      return;
    }
    const action = isArchived ? 'unarchive' : 'archive';
    const msg = isArchived
      ? `Unarchive ${data?.employee?.name}? This will re-activate their account.`
      : `Archive ${data?.employee?.name}? This will deactivate their account immediately.`;
    askConfirm({
      title: isArchived ? 'Unarchive employee' : 'Archive employee',
      message: msg,
      confirmLabel: isArchived ? 'Unarchive' : 'Archive',
      danger: !isArchived,
      onConfirm: async () => {
        setArchiving(true);
        try {
          await axios.post(`${API}/employees/${employeeId}/${action}`);
          showToast(isArchived ? 'Employee unarchived' : 'Employee archived', 'success');
          onBack();
        } catch (err) {
          if (err?.response?.headers?.['x-refusal-reason'] === 'archive_privileged_by_hr') {
            showToast(_privilegedMsg, 'error');
            return;
          }
          showToast('Action failed. Please try again.', 'error');
        } finally {
          setArchiving(false);
        }
      },
    });
  };

  const viewProfileFile = async (type) => {
    try {
      const res = await axios.get(`${API}/employees/${employeeId}/${type}/view`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      showToast('Could not open file. Please try again.', 'error');
    }
  };

  // DLNAME: the download counterpart. The preview above is byte-unchanged; a blob opened in a tab
  // has no filename mechanism at all, which is why a separate control exists. saveBlob applies the
  // SERVER's name, so the file lands as "First Last signature.png" rather than a blob UUID.
  const downloadProfileFile = async (type) => {
    try {
      const res = await axios.get(`${API}/employees/${employeeId}/${type}/view`, { responseType: 'blob' });
      saveBlob(res);
    } catch {
      showToast('Could not download file. Please try again.', 'error');
    }
  };

  const openDocument = async (docId) => {
    setDocLoading(docId);
    try {
      const res = await axios.get(`${API}/documents/${docId}/view`, { responseType: 'blob' });
      // DLNAME: preview unchanged, via the shared helper so every site opens a tab the same way.
      previewBlob(res);
    } catch (err) {
      // DOCREFUSAL: the server's reason when it gave one (an earlier contract, for instance), in
      // the viewer's language; the same sentence as before otherwise, translated.
      alert(refusalCode(err) ? refusalMessage(err, docLang, 'archiveReplace.openFailed')
                             : docText('archiveReplace.openFailed', docLang));
    } finally {
      setDocLoading(null);
    }
  };

  // DLNAME: the download counterpart, named by the server rather than by the blob URL.
  const downloadDocument = async (docId) => {
    try {
      const res = await axios.get(`${API}/documents/${docId}/view`, { responseType: 'blob' });
      saveBlob(res);
    } catch {
      /* the preview control above surfaces the error state for this row */
    }
  };

  const loadData = async () => {
    // LOADERR: each sub-fetch below records a named failure instead of silently pretending the
    // data was absent. A 404 stays silent-normal (no form / no RTW record yet is a real answer).
    // SOFT403: a 403 is likewise NOT a load failure - it is an expected access restriction (e.g.
    // NEW_STARTER_PAYROLL_SCOPE_RELEASED excludes the coo from GET /new-starter/{id}, and the
    // PAYPROFILEVIEW / entity-scoping rules restrict the payroll+delegate view), so surfacing the
    // "Some data failed to load... Retry" banner for it is misleading - retrying changes nothing and
    // nothing is broken. Matches Dashboard.js's formFail exactly. Only a genuine error (500,
    // network/timeout, or any other status) is still recorded.
    const errs = new Set();
    const soft = (key) => (err) => {
      const status = err?.response?.status;
      if (status !== 404 && status !== 403) errs.add(key);
    };
    try {
      const empRes = await axios.get(`${API}/dashboard/employee/${employeeId}`);
      setData(empRes.data);
    } catch (err) {
      console.error(err);
      // A 404 genuinely means "employee not found"; anything else is a load failure and must
      // not masquerade as one (the old behavior rendered "Employee not found" for a server error).
      if (err?.response?.status !== 404) errs.add('profile');
      setLoadErrors(Array.from(errs));
      setLoading(false);
      return;
    }
    try {
      const nsRes = await axios.get(`${API}/new-starter/${employeeId}`);
      setNewStarterForm(nsRes.data);
    } catch (err) { soft('onboarding form')(err); }
    try {
      const lRes = await axios.get(`${API}/leaver/${employeeId}`);
      setLeaverForm(lRes.data);
    } catch (err) { soft('leaver form')(err); }
    try {
      const docsRes = await axios.get(`${API}/documents/employee/${employeeId}`);
      setDocuments(docsRes.data.documents || []);
    } catch (err) { soft('documents')(err); }
    try {
      const empR = await axios.get(`${API}/employees/${employeeId}`);
      setIsArchived(empR.data.is_archived || false);
      setRequiresVisa(!!empR.data.requires_visa);
      setHasResidencePermit(!!empR.data.has_residence_permit);
      setIsFrequentTraveller(!!empR.data.is_frequent_traveller);
      setContractExempt(!!empR.data.contract_exempt);
      setDelegateEmail(empR.data.delegate_email || '');
      setPersonalEmail(empR.data.personal_email || '');
      setSecondaryEntity(empR.data.secondary_entity || '');
      setInternshipEndDate(empR.data.internship_end_date || '');
      setPendingManagerId(empR.data.manager_id || '');
      setTeam(empR.data.function_team || '');
      setEmployeeRaw(empR.data);
    } catch (err) { soft('employee settings')(err); }
    if (hasCooSettings) {
      try {
        const permR = await axios.get(`${API}/employees/${employeeId}/permissions`);
        setCanSubmitNewStarters(!!permR.data.can_submit_new_starters);
      } catch (err) { soft('permissions')(err); }
    }
    setLoadErrors(Array.from(errs));
    setLoading(false);
  };

  const handleTogglePermission = async () => {
    setTogglingPermission(true);
    try {
      const res = await axios.patch(`${API}/employees/${employeeId}/permissions`, {
        can_submit_new_starters: !canSubmitNewStarters,
      });
      setCanSubmitNewStarters(!!res.data.can_submit_new_starters);
      showToast(res.data.can_submit_new_starters ? 'Permission granted' : 'Permission removed', 'success');
    } catch {
      showToast('Could not update permission', 'error');
    } finally {
      setTogglingPermission(false);
    }
  };

  // P45COO: coo/admin marks a UK employee's P45 as not applicable ON THEIR BEHALF - the simple
  // COO-side path. Calls the SAME existing PATCH .../p45-not-applicable endpoint the employee's own
  // NoP45Link uses (which is already gated coo/admin/hr/self server-side), but WITHOUT the HMRC
  // Starter Declaration modal: the Starter Declaration is the employee's own personal attestation
  // and is deliberately NOT captured on their behalf (confirmed decision). The backend completes
  // the pending upload_p45 task and sets p45_not_applicable; loadData() then refreshes the task
  // list and employeeRaw so the button gives way to the "Not applicable" state. EmployeeView.js's
  // NoP45Link/P45DECL flow is entirely separate and untouched.
  const handleMarkP45NotApplicable = async () => {
    setP45Saving(true);
    try {
      await axios.patch(`${API}/employees/${employeeId}/p45-not-applicable`, { p45_not_applicable: true });
      showToast('P45 marked as not applicable', 'success');
      await loadData();
    } catch (err) {
      // UPLOADREFUSE: the server's real reason (e.g. P45 does not apply to this entity).
      showToast(refusalMessage(err, docLang, 'uploadChecks.p45Failed'), 'error');
    } finally {
      setP45Saving(false);
    }
  };

  const handleToggleVisa = async () => {
    setTogglingVisa(true);
    try {
      const res = await axios.patch(`${API}/employees/${employeeId}/requires-visa`, {
        requires_visa: !requiresVisa,
      });
      setRequiresVisa(!!res.data.requires_visa);
      showToast(res.data.requires_visa ? 'Visa sponsorship enabled' : 'Visa sponsorship removed', 'success');
    } catch {
      showToast('Could not update visa status', 'error');
    } finally {
      setTogglingVisa(false);
    }
  };

  const handleTogglePermit = async () => {
    setTogglingPermit(true);
    try {
      const res = await axios.patch(`${API}/employees/${employeeId}/residence-permit`, {
        has_residence_permit: !hasResidencePermit,
      });
      setHasResidencePermit(!!res.data.has_residence_permit);
      showToast(res.data.has_residence_permit ? 'Residence permit / settled status enabled' : 'Residence permit / settled status removed', 'success');
    } catch {
      showToast('Could not update residence permit status', 'error');
    } finally {
      setTogglingPermit(false);
    }
  };

  const handleToggleTraveller = async () => {
    setTogglingTraveller(true);
    try {
      const res = await axios.patch(`${API}/employees/${employeeId}/frequent-traveller`, {
        is_frequent_traveller: !isFrequentTraveller,
      });
      setIsFrequentTraveller(!!res.data.is_frequent_traveller);
      showToast(res.data.is_frequent_traveller ? 'Marked as frequent traveller' : 'Removed from frequent travellers', 'success');
    } catch {
      showToast('Could not update frequent traveller status', 'error');
    } finally {
      setTogglingTraveller(false);
    }
  };

  // CONTRACTEXEMPT2: same plain toggle shape as handleToggleTraveller. The route itself is
  // coo/admin-gated server-side (require_roles), so this handler is only ever reachable from the
  // button below, which is itself only rendered for coo/admin - a defence-in-depth pair, not a
  // single point of trust.
  const handleToggleContractExempt = async () => {
    setTogglingContractExempt(true);
    try {
      const res = await axios.patch(`${API}/employees/${employeeId}/contract-exempt`, {
        contract_exempt: !contractExempt,
      });
      setContractExempt(!!res.data.contract_exempt);
      showToast(res.data.contract_exempt ? 'Marked as exempt from contract upload' : 'Contract upload exemption removed', 'success');
    } catch {
      showToast('Could not update contract exemption', 'error');
    } finally {
      setTogglingContractExempt(false);
    }
  };

  const handleSavePersonalEmail = async () => {
    setSavingPersonalEmail(true);
    try {
      await axios.patch(`${API}/employees/${employeeId}/personal-email`, { personal_email: personalEmail || null });
      showToast('Personal email updated', 'success');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not update personal email', 'error');
    } finally {
      setSavingPersonalEmail(false);
    }
  };

  // CAREERTL: the timeline is its own small fetch, independent of loadData, so a failure here
  // degrades one card rather than the whole profile.
  const loadRoleHistory = useCallback(async () => {
    setRoleHistoryLoading(true);
    try {
      const res = await axios.get(`${API}/employees/${employeeId}/role-history`);
      setRoleHistory(res.data?.entries || []);
      setRoleHistoryError(false);
    } catch {
      setRoleHistory([]);
      setRoleHistoryError(true);
    } finally {
      setRoleHistoryLoading(false);
    }
  }, [employeeId]);

  useEffect(() => { loadRoleHistory(); }, [loadRoleHistory]);

  const handleSaveJobTitle = async () => {
    const trimmed = editJobTitle.trim();
    if (!trimmed) { showToast('Job title is required', 'error'); return; }
    setSavingJobTitle(true);
    try {
      const res = await axios.patch(`${API}/employees/${employeeId}/job-title`, { job_title: trimmed });
      showToast(res.data?.changed ? 'Job title updated' : 'Job title unchanged', 'success');
      setEditingJobTitle(false);
      await Promise.all([loadData(), loadRoleHistory()]);
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not update job title', 'error');
    } finally {
      setSavingJobTitle(false);
    }
  };

  // CONVERTFORM: open the conversion form, pre-filled from the current record. Replaces the old
  // instant one-click confirm. The underlying conversion (background-check task, timeline entry,
  // reminder re-evaluation, intern-field clearing) is UNCHANGED - this only collects the end date,
  // job title and target type first, then calls the same endpoints.
  const openConvertForm = () => {
    setConvertForm({
      internship_end_date: employee?.internship_end_date || '',
      job_title: employee?.job_title || '',
      employee_type: 'employee',   // the AddEmployee default; contractor is the other valid target
    });
    setConvertError(null);
    setShowConvertForm(true);
  };

  const submitConvertForm = async () => {
    setConvertError(null);
    const endDate = (convertForm.internship_end_date || '').trim();
    if (!endDate) {
      setConvertError('Internship end date is required.');
      return;
    }
    setConverting(true);
    try {
      // (a) Save the end date first IF it changed - via the existing endpoint. The conversion
      // itself clears the date afterward, but recording the confirmed end date before converting
      // keeps the transition point on the record for the timeline/history.
      if (endDate !== (employee?.internship_end_date || '')) {
        await axios.patch(`${API}/employees/${employeeId}/internship-end-date`,
          { internship_end_date: endDate });
      }
      // (b) Save the job title IF it changed - CAREERTL's PATCH, which opens a timeline period.
      const newTitle = (convertForm.job_title || '').trim();
      if (newTitle && newTitle !== (employee?.job_title || '')) {
        await axios.patch(`${API}/employees/${employeeId}/job-title`, { job_title: newTitle });
      }
      // (c) The existing conversion, with the chosen target type (CAREERTL already parameterised
      // this). Everything it does - background-check task, timeline, reminder re-eval, field
      // clearing - is untouched.
      const res = await axios.patch(`${API}/employees/${employeeId}/convert-from-intern`,
        { employee_type: convertForm.employee_type });
      showToast(`Converted to ${res.data?.employee_type || convertForm.employee_type}`, 'success');
      setShowConvertForm(false);
      await Promise.all([loadData(), loadRoleHistory()]);
    } catch (err) {
      setConvertError(err.response?.data?.detail || 'Could not convert this employee.');
    } finally {
      setConverting(false);
    }
  };

  const handleSaveName = async () => {
    const trimmedFirst = editFirstName.trim();
    const trimmedLast = editLastName.trim();
    if (!trimmedFirst || !trimmedLast) {
      showToast('First name and last name are required', 'error');
      return;
    }
    setSavingName(true);
    try {
      await axios.patch(`${API}/employees/${employeeId}/name`, { first_name: trimmedFirst, last_name: trimmedLast });
      showToast('Name updated', 'success');
      setEditingName(false);
      await loadData();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not update name', 'error');
    } finally {
      setSavingName(false);
    }
  };

  // EMAILFIX: confirms before firing, because this changes what the employee types to log in.
  // The backend is the real gate (uniqueness, account link, delegation repointing); this only
  // makes the consequence explicit to the operator first.
  const handleSaveCompanyEmail = () => {
    const trimmed = editCompanyEmail.trim().toLowerCase();
    if (!trimmed) {
      showToast('Company email is required', 'error');
      return;
    }
    if (trimmed === (employeeRaw?.company_email || '').toLowerCase()) {
      setEditingCompanyEmail(false);
      return;
    }
    // HRLOGINGUARD: hr may not move the login of a COO or admin account; explained, nothing sent.
    if (role === 'hr' && ['coo', 'admin'].includes(employeeRaw?.account_role)) {
      showToast(docText('docRefusal.login_email_privileged_by_hr', docLang), 'error');
      return;
    }
    askConfirm({
      title: 'Change login email',
      message:
        `This changes ${employee.name}'s login identity. They will need to sign in with `
        + `"${trimmed}" from now on. Their password stays the same, and they will be emailed `
        + 'about the change. Continue?',
      confirmLabel: 'Change login email',
      onConfirm: async () => {
        setSavingCompanyEmail(true);
        try {
          const res = await axios.patch(`${API}/employees/${employeeId}/company-email`, { company_email: trimmed });
          const n = res.data?.delegations_repointed || 0;
          showToast(
            n > 0 ? `Login email updated; ${n} delegation(s) repointed` : 'Login email updated',
            'success',
          );
          setEditingCompanyEmail(false);
          await loadData();
        } catch (err) {
          showToast(refusalCode(err) ? refusalMessage(err, docLang, 'docRefusal.login_email_privileged_by_hr')
                                     : (err.response?.data?.detail || 'Could not update company email'), 'error');
        } finally {
          setSavingCompanyEmail(false);
        }
      },
    });
  };

  const handleSaveDelegateEmail = async () => {
    setSavingDelegate(true);
    try {
      await axios.patch(`${API}/employees/${employeeId}/delegate-email`, { delegate_email: delegateEmail || null });
      showToast('Delegate email updated', 'success');
    } catch {
      showToast('Could not update delegate email', 'error');
    } finally {
      setSavingDelegate(false);
    }
  };

  const handleSaveSecondaryEntity = async () => {
    setSavingSecondaryEntity(true);
    try {
      await axios.patch(`${API}/employees/${employeeId}/secondary-entity`, { secondary_entity: secondaryEntity || null });
      showToast('Secondary entity updated', 'success');
    } catch {
      showToast('Could not update secondary entity', 'error');
    } finally {
      setSavingSecondaryEntity(false);
    }
  };

  const handleSaveInternshipEnd = async () => {
    setSavingInternshipEnd(true);
    try {
      // Empty string -> null clears the date (and, on the backend, removes any internship_end
      // reminder). A set date re-syncs the reminder with the correct short/long preavis rule.
      await axios.patch(`${API}/employees/${employeeId}/internship-end-date`, { internship_end_date: internshipEndDate || null });
      showToast('Internship end date updated', 'success');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not update internship end date', 'error');
    } finally {
      setSavingInternshipEnd(false);
    }
  };

  // MGRPROFILE: `removeManager` true clears the manager (Remove manager). For the COO, admin and HR a
  // Save with no employee chosen is refused here rather than sent as a removal, the server refusal is
  // shown translated beside the field, and the messages are translated.
  const handleSaveManager = async (removeManager = false) => {
    const managerId = removeManager === true ? null : (pendingManagerId || null);
    if (managerFieldFull && removeManager !== true && !managerId) {
      setManagerError(dashT('mgrProfile.errChoose'));
      return;
    }
    setSavingManager(true);
    setManagerError(null);
    try {
      const res = await axios.patch(`${API}/employees/${employeeId}/manager`, { manager_id: managerId });
      // Unlike delegate_email/secondary_entity (displayed straight from their own controlled
      // input), the Manager field above is a derived lookup off employeeRaw.manager_id - so this
      // save must update employeeRaw for that existing useEffect (line ~418) to re-resolve and
      // refresh the read-only display name automatically.
      setEmployeeRaw(prev => (prev ? { ...prev, manager_id: res.data.manager_id } : prev));
      setManagerSearch('');
      setShowManagerList(false);
      showToast(managerFieldFull ? dashT(managerId ? 'mgrProfile.saved' : 'mgrProfile.removed')
        : 'Manager updated', 'success');
    } catch (err) {
      if (managerFieldFull) {
        const detail = err?.response?.data?.detail;
        setManagerError(dashT(MANAGER_ERROR_KEYS[detail] || 'mgrProfile.errSave'));
      } else {
        showToast(err.response?.data?.detail || 'Could not update manager', 'error');
      }
    } finally {
      setSavingManager(false);
    }
  };

  const handleSaveTeam = async () => {
    setSavingTeam(true);
    try {
      const res = await axios.patch(`${API}/employees/${employeeId}/team`, { team: team.trim() || null });
      const saved = res.data?.team || '';
      setTeam(saved);
      setEmployeeRaw(prev => (prev ? { ...prev, function_team: saved || null } : prev));
      if (saved && !teamOptions.some(o => o.toLowerCase() === saved.toLowerCase())) {
        setTeamOptions(prev => [...prev, saved].sort((a, b) => a.localeCompare(b)));
      }
      showToast(dashT('hrProfile.teamSaved'), 'success');
    } catch (err) {
      showToast(err.response?.data?.detail || dashT('hrProfile.teamSaveFailed'), 'error');
    } finally {
      setSavingTeam(false);
    }
  };

  const handleResendDelegateNotification = async () => {
    setResendingDelegate(true);
    try {
      const res = await axios.post(`${API}/employees/${employeeId}/delegate-email/resend`);
      showToast(`Notification sent to ${res.data.sent_to}`, 'success');
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not resend notification', 'error');
    } finally {
      setResendingDelegate(false);
    }
  };

  // ARCHIVEGATE: mirrors POST /documents/{id}/archive's permission rules (documents.py) exactly,
  // so the Archive control is only offered where the endpoint would actually allow it. The
  // backend remains the real gate - this only decides whether to show the button.
  //   coo/admin  -> any document, any type, any employee (unrestricted).
  //   every other role -> the document must be their OWN, and its type must be in
  //                       MANUAL_ARCHIVE_TYPES. (require_self_or_roles' role half can never pass
  //                       in that branch, since coo/admin were already excluded above it, so it
  //                       reduces to a pure self-ownership check.)
  // Previously the Documents table gated on `role === 'coo'`, which wrongly excluded admin, and
  // the per-type list modal had NO gate at all, offering a button that 403s for anyone else.
  const canArchiveDocument = (doc) => {
    if (role === 'coo' || role === 'admin') return true;
    // CONTRACTONE point 3: HR archives or replaces a CONTRACT for any employee, always with the
    // replacement file (needsReplacement below is true for hr, and the server requires it).
    // HRPROFILE follow-up: hr archives every document as the COO does.
    if (role === 'hr') return true;
    const isOwnRecord = Boolean(
      employeeRaw && currentUser && employeeRaw.company_email
      && String(employeeRaw.company_email).trim().toLowerCase() === String(currentUser).trim().toLowerCase()
    );
    return isOwnRecord && MANUAL_ARCHIVE_TYPES.has(doc.type);
  };

  // ARCHREPLACE: for CONTRACT and CV on the employee's OWN record, archiving collects the
  // replacement file in the SAME action - the server requires it (400 otherwise), because
  // archiving alone would leave the record with no active document of that type.
  //
  // WHO GETS THIS FLOW, and who deliberately does NOT:
  //   * the employee on their own record, contract/cv  -> the ArchiveReplaceModal file picker.
  //   * the same employee, passport/visa/proof_of_address/p45 -> the PLAIN confirm dialog,
  //     completely unchanged, because there is a legitimate window where the old document is
  //     void and the new one does not exist yet.
  //   * coo/admin, ANY type -> the PLAIN confirm dialog, completely unchanged. The server never
  //     requires a replacement from them, so requiring one here would invent a restriction the
  //     backend does not have.
  // HRPROFILE follow-up: hr archives like the COO, except a contract, which keeps CONTRACTONE's
  // replacement in the same step.
  const needsReplacement = (doc) =>
    REPLACEMENT_REQUIRED_TYPES.has(doc.type) && role !== 'coo' && role !== 'admin'
    && (role !== 'hr' || doc.type === 'contract');

  const submitArchive = async (docId, file) => {
    setArchivingDoc(docId);
    try {
      let body;
      if (file) {
        body = new FormData();
        body.append('replacement', file);
      }
      await axios.post(`${API}/documents/${docId}/archive`, body);
      showToast(docText(file ? 'archiveReplace.replaced' : 'archiveReplace.archived', docLang), 'success');
      await loadData();
      const remaining = documents.filter(d => d.id !== docId && d.type === docListType && !d.is_archived);
      if (remaining.length < 2) setShowDocList(false);
      setReplaceDoc(null);
    } catch (err) {
      // ARCHIVEERR: surface the server's own reason (e.g. the missing-replacement 400, a blocked
      // extension, a 413) instead of swallowing it.
      showToast(refusalMessage(err, docLang, 'archiveReplace.archiveFailed'), 'error');
    } finally {
      setArchivingDoc(null);
    }
  };

  const handleArchiveDocument = (docId) => {
    const doc = documents.find(d => d.id === docId);
    if (doc && needsReplacement(doc)) {
      setReplaceDoc(doc);
      return;
    }
    askConfirm({
      title: 'Archive document',
      // ARCHIVECOPY: state plainly that this is NOT a deletion, and say where the document goes,
      // so nobody reads "Archive" as "destroy". Matches what the endpoint actually does:
      // storage.archive_document flags the row is_archived and moves the file to Archive/.
      message: 'Archive this document? It is not deleted. The document stays on record and can '
        + 'still be found in the Archive folder, it just stops appearing in the active documents list.',
      confirmLabel: 'Archive',
      danger: true,
      // ARCHREPLACE: no file, so the POST has no body - byte-identical to the request this made
      // before this change, which is what keeps the four other types and coo/admin unchanged.
      onConfirm: () => submitArchive(docId, null),
    });
  };

  // CATFIX: categorize a stray 'uncategorized' document on the employee's behalf via the existing
  // categorize_document endpoint (require_self_or_roles covers coo/admin/hr here). No backend change.
  // CATFIX2: same validation rules as MigrationConfirmation's Step A (its DATE_EXPIRY_TYPES
  // list, reused via export). Also drives the Categorize button's disabled state.
  const categorizeReady = (docId) => {
    const t = categorizeType[docId];
    if (!t) return false;
    const m = categorizeMeta[docId] || {};
    if (DATE_EXPIRY_TYPES.includes(t) && !m.expiry_date) return false;
    if (t === 'passport' && (!(m.passport_number || '').trim() || !(m.passport_country || '').trim())) return false;
    return true;
  };

  const setMeta = (docId, field, value) =>
    setCategorizeMeta(prev => ({ ...prev, [docId]: { ...(prev[docId] || {}), [field]: value } }));

  const categorizeDoc = async (docId) => {
    const newType = categorizeType[docId];
    if (!newType) return;
    const meta = categorizeMeta[docId] || {};
    if (DATE_EXPIRY_TYPES.includes(newType) && !meta.expiry_date) {
      showToast('Please enter the required date for all categorized documents before continuing.', 'error');
      return;
    }
    if (newType === 'passport' && (!(meta.passport_number || '').trim() || !(meta.passport_country || '').trim())) {
      showToast('Please enter the Passport Number and Passport country for all passport documents.', 'error');
      return;
    }
    setCategorizingDoc(docId);
    try {
      const fd = new FormData();
      fd.append('document_type', newType);
      if (DATE_EXPIRY_TYPES.includes(newType) && meta.expiry_date) fd.append('expiry_date', meta.expiry_date);
      if (newType === 'passport') {
        fd.append('passport_number', meta.passport_number.trim());
        fd.append('passport_country', meta.passport_country.trim());
      }
      await axios.patch(`${API}/employees/${employeeId}/documents/${docId}/categorize`, fd);
      showToast('Document categorized', 'success');
      setCategorizeType(prev => { const n = { ...prev }; delete n[docId]; return n; });
      setCategorizeMeta(prev => { const n = { ...prev }; delete n[docId]; return n; });
      await loadData();
    } catch (err) {
      showToast(err.response?.data?.detail || 'Could not categorize document', 'error');
    } finally {
      setCategorizingDoc(null);
    }
  };

  // BGCLEANUP (2026-10-26): no Validate or Reject on a background check any more; the COO has no step
  // in background checks (compliance classifies in 4A, HR uploads in 4B).

  useEffect(() => { loadData(); }, [employeeId]); // eslint-disable-line

  // Load the right-to-work checklist record (only for visa-sponsored employees)
  useEffect(() => {
    if (!employeeId || !requiresVisa) return;
    let cancelled = false;
    axios.get(`${API}/employees/${employeeId}/right-to-work-check`)
      .then(res => { if (!cancelled) { setRtwCheck(res.data); setRtwForm(res.data); } })
      .catch((err) => { if (err?.response?.status !== 404) addLoadError('right to work checklist'); });
    return () => { cancelled = true; };
  }, [employeeId, requiresVisa]);

  // RTWSAVEUX: on a save failure, show the END USER a single simple, reassuring message, and put the
  // REAL technical detail (HTTP status + server-provided detail, or "network timeout / no response")
  // in console.error where a developer can retrieve it from dev tools. This refines RTWSAVEERR, whose
  // catch previously fixed the swallowed-error problem but showed raw server jargon ("Not authorized",
  // "Internal Server Error") on screen - too technical for a non-technical user like Craig. The save
  // failure is still fully diagnosable (nothing is swallowed) - it just lives in the console now, not
  // the toast. `context` labels the console line so it is easy to find. Returns the user-facing
  // message so the caller can toast it. Plain strings, matching this file's showToast convention.
  const USER_SAVE_ERROR = 'Something went wrong saving this. Please try again, or contact support if it keeps happening.';
  const reportSaveError = (err, context) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    const technical = err?.response
      ? `HTTP ${status ?? '?'}${detail ? ` - ${detail}` : ' - (no detail in response)'}`
      : 'network error / no response (timeout or dropped connection)';
    // eslint-disable-next-line no-console
    console.error(`[${context}] save failed: ${technical}`, err);
    return USER_SAVE_ERROR;
  };

  const handleSaveRtw = async () => {
    if (!rtwForm) return;
    setRtwSaving(true);
    try {
      const body = { ...rtwForm, completed_by: currentUser };
      const res = await axios.post(`${API}/employees/${employeeId}/right-to-work-check`, body);
      setRtwCheck(res.data);
      setRtwForm(res.data);
      showToast('Right to work check saved', 'success');
    } catch (err) {
      showToast(reportSaveError(err, 'right-to-work-check'), 'error');
    } finally {
      setRtwSaving(false);
    }
  };

  // Load the "Proof of Right to Work Check Conducted" gov.uk share-code record (only for
  // visa-sponsored employees) - a distinct record from the 8-item checklist above.
  useEffect(() => {
    if (!employeeId || !requiresVisa) return;
    let cancelled = false;
    axios.get(`${API}/employees/${employeeId}/right-to-work-check-conducted`)
      .then(res => { if (!cancelled) { setRtwConducted(res.data); setRtwConductedForm(res.data); } })
      .catch((err) => { if (err?.response?.status !== 404) addLoadError('right to work record'); });
    return () => { cancelled = true; };
  }, [employeeId, requiresVisa]);

  const handleSaveRtwConducted = async () => {
    if (!rtwConductedForm) return;
    setRtwConductedSaving(true);
    try {
      const body = {
        share_code: rtwConductedForm.share_code,
        date_of_birth: rtwConductedForm.date_of_birth,
        completed_by: currentUser,
      };
      const res = await axios.post(`${API}/employees/${employeeId}/right-to-work-check-conducted`, body);
      setRtwConducted(res.data);
      setRtwConductedForm(res.data);
      showToast('Right to work check conducted saved', 'success');
    } catch (err) {
      showToast(reportSaveError(err, 'right-to-work-check-conducted'), 'error');
    } finally {
      setRtwConductedSaving(false);
    }
  };

  useEffect(() => {
    if (!employeeRaw?.manager_id) { setManagerName(null); return; }
    axios.get(`${API}/employees/${employeeRaw.manager_id}`)
      .then(res => setManagerName(`${res.data.first_name} ${res.data.last_name}`))
      .catch(() => setManagerName(null));
  }, [employeeRaw?.manager_id]); // eslint-disable-line

  // MGRPROFILE: for the COO, admin and HR the field starts on the CURRENT manager, so it shows that
  // name instead of the empty search, and Save without a new choice changes nothing.
  useEffect(() => {
    if (!managerFieldFull) return;
    setPendingManagerId(employeeRaw?.manager_id || '');
    setManagerSearch('');
    setManagerError(null);
  }, [employeeRaw?.manager_id, managerFieldFull]); // eslint-disable-line

  // Resolves the display name for the edit control's own pendingManagerId - kept separate from
  // the read-only lookup above so the two never race each other.
  useEffect(() => {
    if (!pendingManagerId) { setManagerSearchName(''); return; }
    axios.get(`${API}/employees/${pendingManagerId}`)
      .then(res => setManagerSearchName(`${res.data.first_name} ${res.data.last_name}`))
      .catch(() => setManagerSearchName(''));
  }, [pendingManagerId]); // eslint-disable-line

  if (loading) return <div style={p.loading}>Loading...</div>;
  // LOADERR: a failed profile load is NOT "Employee not found" - that message is reserved for a
  // genuine 404. Anything else gets the read-failure state with a retry.
  if (!data) {
    return loadErrors.includes('profile') ? (
      <div style={{ padding: '24px' }}>
        <LoadError dark={isDarkMode} message="Couldn't load this employee's profile." onRetry={() => { setLoading(true); loadData(); }} />
      </div>
    ) : (
      <div style={p.loading}>Employee not found</div>
    );
  }

  if (showRecruitment) {
    return (
      <SponsorForm
        employeeId={employeeId}
        employeeName={data.employee.name}
        onBack={() => setShowRecruitment(false)}
        onComplete={() => { setShowRecruitment(false); loadData(); }}
        isAO={true}
        startPhase={3}
      />
    );
  }

  if (showNsSection) {
    const empName = data.employee?.name || '';
    const bcLink = { fontSize: '13px', color: TEXT_MUTED, cursor: 'pointer', transition: 'color 0.12s' };
    const bcSep  = { fontSize: '13px', color: TEXT_MUTED, margin: '0 6px' };
    const bcActive = { fontSize: '13px', color: NAVY_SHELL, fontWeight: 500 };
    return (
      <div>
        <div style={{ marginBottom: '16px' }}>
          <span style={bcLink}
            onMouseEnter={e => e.currentTarget.style.color = GOLD}
            onMouseLeave={e => e.currentTarget.style.color = TEXT_MUTED}
            onClick={onNavigateToNewStarters || (() => setShowNsSection(null))}
          >New Starters</span>
          <span style={bcSep}>›</span>
          <span style={bcLink}
            onMouseEnter={e => e.currentTarget.style.color = GOLD}
            onMouseLeave={e => e.currentTarget.style.color = TEXT_MUTED}
            onClick={() => setShowNsSection(null)}
          >{empName}</span>
          <span style={bcSep}>›</span>
          <span style={bcActive}>New Starter Form</span>
        </div>
        <NewStarterForm
          employeeId={employeeId}
          employeeName={data.employee.name}
          employeeType={data.employee.employee_type}
          employeeEntity={data.employee.primary_entity}
          section={showNsSection}
          completedBy={currentUser}
          currentUserRole={role}
          /* HRS3: hr's way into the form is Section 3 only. Restricting the reachable sections
             stops the progress dots offering Section 2, which the server refuses to hr. */
          allowedSections={role === 'hr' ? [3, 4] : undefined}
          onBack={() => setShowNsSection(null)}
          onNavigate={(n) => setShowNsSection(n)}
          onComplete={() => { setShowNsSection(null); loadData(); }}
        />
      </div>
    );
  }

  if (leaverSection) {
    const empName = data.employee?.name || '';
    const bcLink = { fontSize: '13px', color: TEXT_MUTED, cursor: 'pointer', transition: 'color 0.12s' };
    const bcSep  = { fontSize: '13px', color: TEXT_MUTED, margin: '0 6px' };
    const bcActive = { fontSize: '13px', color: NAVY_SHELL, fontWeight: 500 };
    return (
      <div>
        <div style={{ marginBottom: '16px' }}>
          <span style={bcLink}
            onMouseEnter={e => e.currentTarget.style.color = GOLD}
            onMouseLeave={e => e.currentTarget.style.color = TEXT_MUTED}
            onClick={onNavigateToEmployees || (() => setLeaverSection(null))}
          >Employees</span>
          <span style={bcSep}>›</span>
          <span style={bcLink}
            onMouseEnter={e => e.currentTarget.style.color = GOLD}
            onMouseLeave={e => e.currentTarget.style.color = TEXT_MUTED}
            onClick={() => setLeaverSection(null)}
          >{empName}</span>
          <span style={bcSep}>›</span>
          <span style={bcActive}>Leaver Form</span>
        </div>
        <LeaverForm
          employeeId={employeeId}
          employeeName={data.employee.name}
          requiresVisa={data.employee.requires_visa}
          location={data.employee.location}
          section={leaverSection}
          completedBy={currentUser}
          /* HRLEAVER: hr completes Sections 1 and 3; the IT section (2) stays with the COO. */
          /* SASCOPE: compliance no longer fills the leaver IT section either. */
          allowedSections={(role === 'hr' || role === 'compliance') ? [1, 3] : undefined}
          onBack={() => setLeaverSection(null)}
          onNavigate={(n) => setLeaverSection(n)}
          onComplete={() => { setLeaverSection(null); loadData(); }}
        />
      </div>
    );
  }

  const { employee, sponsor_form, tasks, reminders } = data;
  const phase2Done = sponsor_form?.phase2_completed;
  const recruitmentDone = sponsor_form?.status === 'complete';
  const leaverStarted = leaverForm?.section1_completed;
  const leaverComplete = leaverForm?.status === 'complete';
  // LEAVERDRAFT: a leaver form row exists but Section 1 was only saved as a draft, never
  // submitted. Distinct from "never started" (no row at all); "started" (leaverStarted above)
  // keeps meaning exactly section1_completed=true, per the confirmed COO decision.
  const leaverDraft = !!leaverForm && !leaverForm.section1_completed;
  // INTERNVIS: an intern whose internship end date is today or past, not yet archived and without a
  // leaver process started - the exact state INTERNOFFBOARD's COO task points at. Drives the "start
  // offboarding" call-to-action below, which reuses the existing setLeaverSection(1) leaver flow.
  // INTERNENDSHARED: the "ended" decision now comes from the ONE shared timezone-safe helper
  // (internship.js), identical to Dashboard's Interns badge and the backend job, so the CTA here can
  // never disagree with the Dashboard on the boundary day. Was: new Date(bare YYYY-MM-DD) parsed at
  // UTC midnight and compared to now(), which fired a day early for viewers west of UTC.
  const internEnded = employee?.employee_type === 'intern'
    && isInternshipEnded(employee?.internship_end_date) && !isArchived && !leaverStarted;

  const nsStatus = (done) => done
    ? <span style={{ color: '#2D4A3E', fontWeight: 600 }}>✓ Done</span>
    : <span style={{ color: GOLD, fontWeight: 600 }}>Pending</span>;

  // IT (S2) and Payroll (S3) lock independently now - S2 locks the moment it's finally
  // submitted (it_email_sent fires at that same instant), S3 has no it_email_sent-based
  // reopening at all (Payroll's own original completed/not-completed behavior, untouched).
  // S1ONEROW (confirmed decision by Marie-Lynne, supersedes S1SPLIT's two-row display): Section 1
  // shows as ONE row, because it is one section. S1SPLIT's two rows read as two separate tasks.
  //
  // THE FORM IS UNTOUCHED. Part A and Part B remain two steps with their own endpoints
  // (?part=a / ?part=b) and their own completion flags; NewStarterForm's two-step stepper is not
  // changed. This is display only.
  //
  // `done` reads the COMBINED section1_completed, which the backend already derives as exactly
  // this (new_starter.py: section1_completed = bool(section1_completed_at AND
  // section1b_completed_at)). So no derivation is re-implemented here and none is changed - the
  // row shows the flag that already means "both parts done".
  //
  // PARTIAL STATE: one part done and the other not must read as neither complete nor untouched,
  // because somebody has already done half of it. s1Partial drives a third visual state (see the
  // row renderer): a half-filled badge and "Part A done, Part B pending", rather than the bare
  // "Pending" every other incomplete row shows. The two timestamps stay available on the row so
  // the partial line can name WHICH half is outstanding.
  //
  // No fillSection: Section 1 is the employee's OWN form, filled from their own shell, and it
  // carried no action control before this change either (s1a and s1b were both fillSection: null).
  // Merging the rows deliberately does not invent one.
  const _s1aAt = newStarterForm?.section1_completed_at;
  const _s1bAt = newStarterForm?.section1b_completed_at;
  const _s1Done = !!newStarterForm?.section1_completed;
  const nsSections = [
    { key: 's1', label: 'Personal & Financial', done: _s1Done, by: null, at: (_s1aAt && _s1bAt) ? _s1bAt : null, fillSection: null,
      partial: !_s1Done && !!(_s1aAt || _s1bAt),
      partialLabel: _s1aAt && !_s1bAt ? 'Personal Info done, Financial & Tax pending'
        : _s1bAt && !_s1aAt ? 'Financial & Tax done, Personal Info pending'
        : null },
    { key: 's2', label: 'IT Setup',          done: newStarterForm?.section2_completed, by: newStarterForm?.section2_completed_by, at: newStarterForm?.section2_completed_at, fillSection: 2 },
    { key: 's3', label: 'Payroll & Benefits',done: newStarterForm?.section3_completed, by: newStarterForm?.section3_completed_by, at: newStarterForm?.section3_completed_at, fillSection: 3 },
    // S4AB: Section 4 is two rows. 4A is compliance's (filled from the compliance screen, as
    // before); 4B is HR's upload, which HR, the COO and admin open from here.
    { key: 's4a', label: docText('section4ab.title4a', docLang), done: s4aDone(newStarterForm), by: newStarterForm?.section4a_completed_by || newStarterForm?.section4_completed_by, at: newStarterForm?.section4a_completed_at || newStarterForm?.section4_completed_at, fillSection: null },
    { key: 's4b', label: docText('section4ab.title4b', docLang), done: s4bDone(newStarterForm), by: null, at: newStarterForm?.section4b_completed_at || (s4bDone(newStarterForm) ? newStarterForm?.section4_completed_at : null), fillSection: (['hr', 'coo', 'admin'].includes(role) && !employeeRaw?.migration_confirmed) ? 4 : null },
  ];
  const completedCount = nsSections.filter(s => s.done).length;
  const pct = Math.round((completedCount / nsSections.length) * 100);
  // CLPROFILE (confirmed COO decision, supersedes PAYPROFILEVIEW's trim): a Country Lead now sees
  // the SAME profile the COO sees for their own entity, so all 5 onboarding rows render - they run
  // the entity and need the true state of every section, not a payroll-only slice.
  //
  // The ONE control deliberately withheld is "Fill IT Setup": Section 2 belongs to the COO. It is
  // suppressed by clearing s2's fillSection (the button renders only when fillSection is set), NOT
  // by hiding the row - so the Country Lead can still SEE whether IT Setup is done, which is
  // information they need, while being unable to act on it. s4 (Compliance) already carries
  // fillSection: null for everyone, so it needs no special handling here.
  // CLVIEW: internal-only documents (the IT Setup XLSX) are never listed on this profile for a
  // Country Lead. EmployeeView.js has filtered internal_only out of its own documents table since
  // L-2; this view had no such filter, so the row rendered here.
  //
  // THE SERVER IS THE CONTROL, NOT THIS. documents.py refuses both the LIST row and the FILE for a
  // Country Lead, so the row no longer arrives. This filter is defence in depth: it keeps the two
  // document tables consistent, and it means a future widening of the API cannot surface an
  // internal-only row here by surprise.
  //
  // SCOPED TO THE DELEGATE VIEW so coo/admin/hr keep the full list, including internal_only rows,
  // exactly as the backend comment for the employee-role filter says they should.
  const visibleDocuments = isPayrollDelegateView
    ? (documents || []).filter(d => !d.internal_only)
    : documents;

  // HRSHELL point 5: for an hr account, the two onboarding sections the SERVER refuses to hr lose
  // their Fill button, by the SAME mechanism the Country Lead case above uses (clear fillSection,
  // keep the row), so HR still SEES whether each section is done - which is HR's job - without
  // being offered a control that would fail on click.
  //
  //   s2 "IT Setup"           - submit_section2 is gated coo/admin/hr on paper, but the task that
  //                             drives it is a COO task and the brief names IT setup as one of the
  //                             sections to hide from HR. Hidden.
  //   s3 "Payroll & Benefits" - the payroll section. submit_section3 admits hr only where hr is
  //                             the resolved payroll contact for that employee's entity, so the
  //                             button would 403 for every other employee. Hidden outright rather
  //                             than conditionally, because the intended behaviour excludes
  //                             Payroll from HR altogether and the per-entity exception is one of
  //                             the breaches the investigation flagged for a separate build.
  //
  // s1 keeps its row (fillSection is already null for everyone) and s4 (Compliance) likewise.
  //
  // HRS3 (confirmed COO decision, 2026-10-19): s3 is NO LONGER hidden. HR fills Payroll &
  // Benefits for every entity, and the server now admits it (submit_section3, may_write_section3).
  // s2 stays hidden: HR never sees or fills the IT section, and the server now refuses it too.
  const _hrHiddenFill = new Set(['s2']);
  const visibleNsSections = isPayrollDelegateView
    ? nsSections.map(s => (s.key === 's2' ? { ...s, fillSection: null } : s))
    : role === 'hr'
      ? nsSections.map(s => (_hrHiddenFill.has(s.key) ? { ...s, fillSection: null } : s))
      : nsSections;

  // 30-day upcoming window, mirroring EmployeeView.js's urgentTasks/upcomingReminders
  // (commit 5f7410d).
  // TASKHIDEDONE (confirmed COO decision): completed tasks are no longer in the default list -
  // "Over time this is going to become quite long... I see no benefit to having them listed."
  // They stay in the DATABASE untouched (audit trail) and remain viewable via the
  // "Show completed" toggle; only the default display changed. The card title's count reflects
  // the visible open tasks only.
  // TASKOVERDUE: overdue tasks first (they always pass the 30-day window), then by due date.
  const openTasks = sortPendingTasks(tasks.filter(t => {
    if (t.status !== 'pending') return false;
    if (!t.due_date) return true;
    const daysUntilDue = (new Date(t.due_date) - Date.now()) / (1000 * 60 * 60 * 24);
    return daysUntilDue <= 30;
  }));
  const completedTasks = tasks.filter(t => t.status === 'completed');

  // TASKACTOR: this panel is selected by Task.employee_id, so it lists every task ABOUT this
  // person: their own onboarding tasks AND the tasks compliance, HR or the COO must do ON them.
  // Before this, the two were indistinguishable - "Upload background check for <name>" sat beside
  // "Upload passport" with nothing saying who was being waited on.
  //
  // THE ORDER IS THE SERVER'S, not a second copy of it: ACTOR_ORDER in services/task_actor.py
  // defines "the employee's own tasks first", and this mirrors that one list. A task whose actor
  // could not be resolved (unassigned, or an assignee with no account) sorts last and renders as
  // Unassigned rather than being hidden or guessed at.
  // REGOLD point 2c (2026-10-16): admin and ceo KEPT from the removal build. They are a
  // LABEL fix, not a colour one - both rendered "Unassigned" although the task was assigned -
  // so the correction leaves them in place. Appended, matching the server's ALL_ACTORS, so no
  // existing group was reshuffled.
  const ACTOR_ORDER = ['employee', 'compliance', 'hr', 'coo', 'country_lead', 'payroll',
                       'finance', 'admin', 'ceo'];
  const actorRank = (a) => {
    const i = ACTOR_ORDER.indexOf(a);
    return i === -1 ? ACTOR_ORDER.length : i;
  };
  // A plain computation, NOT useMemo: openTasks is derived after an early return in this
  // component, so a hook here would be called conditionally and break the rules of hooks (the
  // build caught exactly that). The list is one employee's open tasks, so there is nothing to
  // memoise away.
  const groupedOpenTasks = (() => {
    const groups = new Map();
    for (const t of openTasks) {
      const key = t.actor || '__unknown__';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(t);
    }
    return [...groups.entries()]
      .sort((x, y) => actorRank(x[0]) - actorRank(y[0]))
      .map(([actor, items]) => ({ actor, items }));
  })();

  const actorLabel = (a) => (a && a !== '__unknown__'
    ? dashT(`tasks.actor.${a}`, a)
    : dashT('tasks.actorUnknown'));
  const upcomingReminders = reminders.filter(r => {
    if (!r.due_date) return true;
    const daysUntilDue = (new Date(r.due_date) - Date.now()) / (1000 * 60 * 60 * 24);
    return daysUntilDue <= 30;
  });

  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif', background: isDarkMode ? '#0f1a2e' : '#edecea' }}>
      {confirmModal}

      {/* ARCHREPLACE: the archive-and-replace dialog, opened ONLY for contract/cv on the
          employee's own record (see handleArchiveDocument). Every other archive still goes
          through {confirmModal} above, unchanged. */}
      <ArchiveReplaceModal
        open={!!replaceDoc}
        dark={isDarkMode}
        typeLabel={replaceDoc ? documentTypeLabel(replaceDoc.type) : ''}
        documentType={replaceDoc ? replaceDoc.type : undefined}
        busy={!!replaceDoc && archivingDoc === replaceDoc.id}
        onCancel={() => setReplaceDoc(null)}
        onConfirm={(file) => submitArchive(replaceDoc.id, file)}
      />

      {/* LOADERR: page-level read-failure banner naming exactly which of this profile's loads
          failed, so a card below can never pass a server failure off as absent data. */}
      {loadErrors.length > 0 && (
        <div style={{ padding: '16px 16px 0' }}>
          <LoadError
            dark={isDarkMode}
            message={`Some of this profile's data failed to load (${loadErrors.join(', ')}) - the cards below may be incomplete.`}
            onRetry={() => { setLoading(true); loadData(); }}
          />
        </div>
      )}

      {/* ── Visa Sponsored Banner ── (PAYPROFILEVIEW: hidden for the payroll+delegate view) */}
      {requiresVisa && (
        <div style={{ background: NAVY_SHELL, color: GOLD, textAlign: 'center', padding: '18px 24px', letterSpacing: '0.2em', fontSize: '17px', fontWeight: 800, textTransform: 'uppercase', borderBottom: `3px solid ${GOLD}` }}>
          VISA SPONSORED EMPLOYEE
        </div>
      )}

      <div style={{ padding: '16px' }}>

        {/* ── Sponsorship Compliance Checklist (visa employees only - moved to top) ── */}
        {requiresVisa && (() => {
          const getDoc    = (type) => documents.find(d => d.type === type && d.is_current && !d.is_archived);
          const hasDoc    = (type) => !!getDoc(type);
          const niOk      = !!(newStarterForm?.ni_number || employeeRaw?.ni_number);
          const contactOk = !!(newStarterForm?.personal_email || employeeRaw?.mobile);
          const CheckItem = ({ label, ok, docType, onClickNav }) => {
            const allDocs = docType ? documents.filter(d => d.type === docType && !d.is_archived) : [];
            const count = allDocs.length;
            const isNavItem = !docType && !!onClickNav;
            const handleView = (e) => {
              e.stopPropagation();
              if (count === 1) { openDocument(allDocs[0].id); }
              else { setDocListType(docType); setShowDocList(true); }
            };
            const handleUpload = (e) => {
              e.stopPropagation();
              setUploadDocType(docType);
              setShowUpload(true);
            };
            return (
              <div
                onClick={isNavItem ? onClickNav : undefined}
                onMouseEnter={isNavItem ? (e => { e.currentTarget.style.backgroundColor = 'rgba(201,168,76,0.06)'; }) : undefined}
                onMouseLeave={isNavItem ? (e => { e.currentTarget.style.backgroundColor = ''; }) : undefined}
                style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 4px', borderBottom: '0.5px solid rgba(26,39,68,0.06)', cursor: isNavItem ? 'pointer' : 'default', transition: 'background-color 0.12s', borderRadius: '4px' }}
              >
                <span style={{ width: '20px', height: '20px', borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: ok ? '#E8F3EE' : '#f0f0f0', color: ok ? '#2D4A3E' : '#bbb', fontSize: '12px', fontWeight: 700 }}>
                  {ok ? '✓' : '○'}
                </span>
                <span style={{ fontSize: '13px', color: ok ? NAVY_SHELL : TEXT_MUTED, flex: 1 }}>{label}</span>
                {docType && count > 0 && (
                  <button
                    onClick={handleView}
                    style={{ fontSize: '11px', padding: '3px 8px', border: `1px solid ${NAVY_SHELL}`, borderRadius: '4px', background: NAVY_SHELL, color: '#fff', cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}
                  >
                    {count > 1 ? `View (${count})` : 'View'}
                  </button>
                )}
                {/* HRVISAALL, kept by HRPROFILE: hr views every checklist document but its Upload
                    buttons stay the COO's. */}
                {docType && role !== 'hr' && (
                  <button
                    onClick={handleUpload}
                    style={{ fontSize: '11px', padding: '3px 8px', border: `1px solid ${GOLD}`, borderRadius: '4px', background: 'transparent', color: GOLD, cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}
                  >
                    Upload
                  </button>
                )}
              </div>
            );
          };
          const rtwSavedAll = !!rtwCheck && RTW_FIELDS.every(([k]) => rtwCheck[k]) && !!rtwCheck.check_date;
          const rtwSavedAny = !!rtwCheck && (RTW_FIELDS.some(([k]) => rtwCheck[k]) || !!rtwCheck.check_date);
          const rtwColor = rtwSavedAll ? '#2D4A3E' : rtwSavedAny ? GOLD : '#bbb';
          const rtwBg    = rtwSavedAll ? '#E8F3EE' : rtwSavedAny ? '#FFF8EC' : '#f0f0f0';
          const rtwIcon  = rtwSavedAll ? '✓' : rtwSavedAny ? '◐' : '○';
          // RTWHR (2026-10-27, supersedes HRVISAALL's read only view): HR performs the right to work
          // check as the COO does: the ticks, the date, the share code and date of birth, both Save
          // buttons and the certificate upload. The other checklist items keep their HRPROFILE rule.
          const canEditRtw = role === 'coo' || role === 'admin' || role === 'hr';
          return (
            <div style={p.card}>
              <p style={p.sectionTitle}>Sponsorship Compliance Checklist</p>
              <CheckItem label="ID (passport)"                                        ok={hasDoc('passport')}                  docType="passport" />
              <CheckItem label="NI / National ID number"                             ok={niOk}                                docType={null} onClickNav={scrollToNi} />
              <CheckItem label="CV and proof of qualifications"                      ok={hasDoc('cv')}                        docType="cv" />
              <CheckItem label="Up to date contact details"                          ok={contactOk}                           docType={null} onClickNav={scrollToContact} />
              <CheckItem label="Signed employment contract"                          ok={hasDoc('contract')}                  docType="contract" />
              <CheckItem label="Sponsorship documentation (visa)"                    ok={hasDoc('visa')}                      docType="visa" />

              {/* Right to Work - expandable, two sub-tabs */}
              {/* RTWDEEPLINKFIX: ref so the upload_right_to_work task deep-link can scroll here (and
                  the effect expands it), instead of opening the dead-end generic right_to_work modal. */}
              <div
                ref={el => { cardRefs.current.right_to_work = el; }}
                style={{ borderBottom: '0.5px solid rgba(26,39,68,0.06)', ...(highlightCard === 'right_to_work' ? CARD_HIGHLIGHT : {}) }}>
                <div
                  onClick={() => setRtwExpanded(v => !v)}
                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = 'rgba(201,168,76,0.06)'; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = ''; }}
                  style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 4px', cursor: 'pointer', transition: 'background-color 0.12s', borderRadius: '4px' }}
                >
                  <span style={{ width: '20px', height: '20px', borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: rtwBg, color: rtwColor, fontSize: '12px', fontWeight: 700 }}>
                    {rtwIcon}
                  </span>
                  <span style={{ fontSize: '13px', color: rtwSavedAll ? NAVY_SHELL : TEXT_MUTED, flex: 1 }}>Right to Work</span>
                  <span style={{ fontSize: '11px', color: TEXT_MUTED, flexShrink: 0 }}>{rtwExpanded ? '▲' : '▼'}</span>
                </div>
                {rtwExpanded && (
                  <div style={{ padding: '2px 4px 14px 32px' }}>
                    <div style={{ display: 'flex', gap: '6px', marginBottom: '14px', borderBottom: '0.5px solid rgba(26,39,68,0.08)' }}>
                      {[
                        ['document',      'Proof of Right to Work Document'],
                        ['checkConducted','Proof of Right to Work Check Conducted'],
                      ].map(([key, label]) => (
                        <button
                          key={key}
                          onClick={() => setRtwSubTab(key)}
                          style={{
                            fontSize: '12px', padding: '7px 10px', border: 'none', borderBottom: rtwSubTab === key ? `2px solid ${GOLD}` : '2px solid transparent',
                            background: 'transparent', color: rtwSubTab === key ? NAVY_SHELL : TEXT_MUTED, fontWeight: rtwSubTab === key ? 600 : 400, cursor: 'pointer',
                          }}
                        >
                          {label}
                        </button>
                      ))}
                    </div>

                    {rtwSubTab === 'document' && rtwForm && (
                      <div>
                        {RTW_FIELDS.map(([key, label]) => (
                          <label key={key} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '5px 0', fontSize: '13px', color: NAVY_SHELL, cursor: canEditRtw ? 'pointer' : 'default' }}>
                            <input
                              type="checkbox"
                              checked={!!rtwForm[key]}
                              disabled={!canEditRtw}
                              onChange={e => setRtwForm(f => ({ ...f, [key]: e.target.checked }))}
                              style={{ marginTop: '2px', flexShrink: 0 }}
                            />
                            <span>{label}</span>
                          </label>
                        ))}
                        <div style={{ marginTop: '10px' }}>
                          <label style={{ display: 'block', fontSize: '10px', color: TEXT_MUTED, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.06em' }} htmlFor={`${a11yId}-date-of-right-to-work-check`}>Date of right to work check</label>
                          
                          <input id={`${a11yId}-date-of-right-to-work-check`}
                            type="date"
                            value={rtwForm.check_date || ''}
                            disabled={!canEditRtw}
                            onChange={e => setRtwForm(f => ({ ...f, check_date: e.target.value || null }))}
                            style={{ fontSize: '13px', padding: '6px 8px', border: '0.5px solid rgba(26,39,68,0.2)', borderRadius: '6px' }}
                          />
                        </div>
                        {canEditRtw && (
                          <div style={{ marginTop: '12px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                            <button
                              onClick={handleSaveRtw}
                              disabled={rtwSaving}
                              style={{ fontSize: '12px', padding: '6px 16px', border: 'none', borderRadius: '6px', background: NAVY_SHELL, color: '#fff', cursor: rtwSaving ? 'wait' : 'pointer' }}
                            >
                              {rtwSaving ? 'Saving…' : 'Save'}
                            </button>
                            {rtwCheck?.updated_at && (
                              <span style={{ fontSize: '11px', color: TEXT_MUTED }}>
                                Last saved {toDisplayDate(rtwCheck.updated_at).toLocaleDateString()}{rtwCheck.completed_by ? ` · ${rtwCheck.completed_by}` : ''}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                    )}

                    {rtwSubTab === 'checkConducted' && (() => {
                      const evidenceDocs = documents.filter(d => d.type === 'right_to_work_check_conducted' && !d.is_archived);
                      return (
                        <div>
                          <p style={{ fontSize: '12px', color: TEXT_MUTED, margin: '0 0 12px 0' }}>
                            Perform the check at{' '}
                            <a href="https://www.gov.uk/view-right-to-work" target="_blank" rel="noopener noreferrer" style={{ color: GOLD }}>
                              gov.uk/view-right-to-work
                            </a>
                            , then record the details below and upload the certificate it generates as proof the check was conducted.
                          </p>
                          {rtwConductedForm && (
                            <>
                              <div style={{ marginBottom: '10px' }}>
                                <label style={{ display: 'block', fontSize: '10px', color: TEXT_MUTED, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.06em' }} htmlFor={`${a11yId}-share-code`}>Share code</label>
                                
                                <input id={`${a11yId}-share-code`}
                                  type="text"
                                  value={rtwConductedForm.share_code || ''}
                                  disabled={!canEditRtw}
                                  onChange={e => setRtwConductedForm(f => ({ ...f, share_code: e.target.value || null }))}
                                  placeholder="e.g. A1B 2C3 D4E"
                                  style={{ fontSize: '13px', padding: '6px 8px', border: '0.5px solid rgba(26,39,68,0.2)', borderRadius: '6px', width: '200px' }}
                                />
                              </div>
                              <div style={{ marginBottom: '10px' }}>
                                <label style={{ display: 'block', fontSize: '10px', color: TEXT_MUTED, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.06em' }} htmlFor={`${a11yId}-employee-date-of-birth`}>Employee date of birth</label>
                                
                                <input id={`${a11yId}-employee-date-of-birth`}
                                  type="date"
                                  value={rtwConductedForm.date_of_birth || ''}
                                  disabled={!canEditRtw}
                                  onChange={e => setRtwConductedForm(f => ({ ...f, date_of_birth: e.target.value || null }))}
                                  style={{ fontSize: '13px', padding: '6px 8px', border: '0.5px solid rgba(26,39,68,0.2)', borderRadius: '6px' }}
                                />
                                <p style={{ fontSize: '11px', color: TEXT_MUTED, margin: '4px 0 0 0' }}>Pre-filled from the employee's record - confirm or correct as needed.</p>
                              </div>
                              {canEditRtw && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' }}>
                                  <button
                                    onClick={handleSaveRtwConducted}
                                    disabled={rtwConductedSaving}
                                    style={{ fontSize: '12px', padding: '6px 16px', border: 'none', borderRadius: '6px', background: NAVY_SHELL, color: '#fff', cursor: rtwConductedSaving ? 'wait' : 'pointer' }}
                                  >
                                    {rtwConductedSaving ? 'Saving…' : 'Save'}
                                  </button>
                                  {rtwConducted?.updated_at && (
                                    <span style={{ fontSize: '11px', color: TEXT_MUTED }}>
                                      Last saved {toDisplayDate(rtwConducted.updated_at).toLocaleDateString()}{rtwConducted.completed_by ? ` · ${rtwConducted.completed_by}` : ''}
                                    </span>
                                  )}
                                </div>
                              )}
                            </>
                          )}
                          <div>
                            <label style={{ display: 'block', fontSize: '10px', color: TEXT_MUTED, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Check certificate</label>
                            {evidenceDocs.length === 0 ? (
                              <p style={{ fontSize: '13px', color: TEXT_MUTED, fontStyle: 'italic', margin: '0 0 8px 0' }}>No certificate uploaded yet.</p>
                            ) : evidenceDocs.map(doc => (
                              <div key={doc.id} style={{ marginBottom: '6px' }}>
                                <button onClick={() => openDocument(doc.id)} disabled={docLoading === doc.id} style={p.docLink}>
                                  {docLoading === doc.id ? 'Opening…' : doc.file_name}
                                </button>
                                {/* DLNAME: download under the readable server name, beside the preview. */}
                                <button onClick={() => downloadDocument(doc.id)} style={p.docLink}>Download</button>
                              </div>
                            ))}
                            {canEditRtw && (
                              <button
                                onClick={() => { setUploadDocType('right_to_work_check_conducted'); setShowUpload(true); }}
                                style={{ fontSize: '11px', padding: '5px 10px', border: `1px solid ${GOLD}`, borderRadius: '4px', background: 'transparent', color: GOLD, cursor: 'pointer' }}
                              >
                                Upload certificate
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>

              <CheckItem label="Home Office decision letter"                         ok={hasDoc('home_office_letter')}        docType="home_office_letter" />
              <CheckItem label="Evidence of entry into UK since start of sponsorship" ok={hasDoc('visa_stamp')}               docType="visa_stamp" />
              <CheckItem label="Job advertisement retained"                          ok={hasDoc('job_advertisement')}         docType="job_advertisement" />
            </div>
          );
        })()}

        {/* ── Right to Work Verification (Employer Checking Service) - parallel path to the
             gov.uk share-code check above, for employees who do not require visa sponsorship,
             are UK-resident, AND are not a British national. Mutually exclusive with the visa
             checklist above: isEligibleForEmployerCheckingServiceCheck already requires
             !requiresVisa, so the two sections can never both render for the same employee (the
             added nationality condition only narrows the non-visa case further, it doesn't affect
             that guarantee). Staff-only (coo/admin), same restriction as the visa path. Reuses the
             same right_to_work_check_conducted document type/upload mechanism - no
             share_code/date_of_birth fields and no recurring reminder, this is a one-time
             verification only. ── */}
        {isEligibleForEmployerCheckingServiceCheck(employeeRaw, requiresVisa) && (() => {
          const evidenceDocs = documents.filter(d => d.type === 'right_to_work_check_conducted' && !d.is_archived);
          const canEditRtw = role === 'coo' || role === 'admin';
          return (
            <div style={p.card}>
              <p style={p.sectionTitle}>Right to Work Verification</p>
              <p style={{ fontSize: '12px', color: TEXT_MUTED, margin: '0 0 12px 0' }}>
                This employee does not require visa sponsorship. Verify their right to work using the gov.uk{' '}
                <a href="https://www.gov.uk/employee-immigration-employment-status" target="_blank" rel="noopener noreferrer" style={{ color: GOLD }}>
                  Employer Checking Service
                </a>
                , then upload the certificate it generates as proof the check was conducted.
              </p>
              <div>
                <label style={{ display: 'block', fontSize: '10px', color: TEXT_MUTED, marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Check certificate</label>
                {evidenceDocs.length === 0 ? (
                  <p style={{ fontSize: '13px', color: TEXT_MUTED, fontStyle: 'italic', margin: '0 0 8px 0' }}>No certificate uploaded yet.</p>
                ) : evidenceDocs.map(doc => (
                  <div key={doc.id} style={{ marginBottom: '6px' }}>
                    <button onClick={() => openDocument(doc.id)} disabled={docLoading === doc.id} style={p.docLink}>
                      {docLoading === doc.id ? 'Opening…' : doc.file_name}
                    </button>
                    {/* DLNAME: download under the readable server name, beside the preview. */}
                    <button onClick={() => downloadDocument(doc.id)} style={p.docLink}>Download</button>
                  </div>
                ))}
                {canEditRtw && (
                  <button
                    onClick={() => { setUploadDocType('right_to_work_check_conducted'); setShowUpload(true); }}
                    style={{ fontSize: '11px', padding: '5px 10px', border: `1px solid ${GOLD}`, borderRadius: '4px', background: 'transparent', color: GOLD, cursor: 'pointer' }}
                  >
                    Upload certificate
                  </button>
                )}
              </div>
            </div>
          );
        })()}

        {/* ── RTW: British nationals gap note - this is the case newly excluded from the
             Employer Checking Service path above (!requiresVisa && UK-resident && British
             national): they don't need the visa checklist (no visa required) or the Employer
             Checking Service (British nationals aren't in scope for it), so without this note
             they would see NEITHER right to work card with no explanation. Informational only -
             no action/upload here, since there is currently nothing for Compliance to do for this
             group; per this session's own context, British nationals are expected to be covered
             by the Home Office's Digital Verification Service once available (from October). ── */}
        {!requiresVisa && isUkResidentCountry(employeeRaw) && isBritishNational(employeeRaw) && (
          <div style={p.card}>
            <p style={p.sectionTitle}>Right to Work Verification</p>
            <p style={{ fontSize: '12px', color: TEXT_MUTED, margin: 0 }}>
              This employee is a British national and does not require visa sponsorship, so no
              manual right to work verification is needed here. British nationals will be covered
              by the Home Office's Digital Verification Service once available (from October).
            </p>
          </div>
        )}

        {/* ── Header card ── */}
        <div style={p.card}>
          {/* CARDHEAD: this row used to be a plain non-wrapping flex with an UNSHRINKABLE button
              cluster (flexShrink: 0) next to a name block set to flex: 1. Below roughly 1000px
              there was no longer room for both, and because the row could not wrap and the buttons
              could not shrink, the name block was squeezed toward zero: the job title, entity,
              login email and visa lines each collapsed into a one-word-per-line column and the
              buttons rode over the name. flexWrap is the fix - the buttons now move to their own
              line instead of stealing width - and the name block carries a flexBasis so it claims
              its full width and triggers that wrap, rather than shrinking first. */}
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', flexWrap: 'wrap' }}>
            <div style={p.avatar}>{employee.name.charAt(0)}</div>
            {/* CARDHEAD: flexBasis is what makes the wrap happen at the right moment. With only
                flex: 1 the block would still shrink to the leftovers of the button cluster;
                demanding NAME_MIN_BASIS as its ideal width means that once the buttons no longer
                fit beside it, the whole cluster wraps below. minWidth: 0 is kept so long words can
                ellipsize rather than force the row wider than the card. */}
            <div style={{ flex: `1 1 ${NAME_MIN_BASIS}px`, minWidth: 0 }}>
              {editingName ? (
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <Labeled label={dashT('profileFields.first_name')} dark={isDarkMode}><input
                    type="text"
                    value={editFirstName}
                    onChange={e => setEditFirstName(e.target.value)}
                    placeholder="First name"
                    style={{ fontSize: '14px', border: '1px solid #ddd', borderRadius: '6px', padding: '6px 10px', width: '140px', outline: 'none', color: NAVY_SHELL, background: '#fff' }}
                  /></Labeled>
                  <Labeled label={dashT('profileFields.last_name')} dark={isDarkMode}><input
                    type="text"
                    value={editLastName}
                    onChange={e => setEditLastName(e.target.value)}
                    placeholder="Last name"
                    style={{ fontSize: '14px', border: '1px solid #ddd', borderRadius: '6px', padding: '6px 10px', width: '140px', outline: 'none', color: NAVY_SHELL, background: '#fff' }}
                  /></Labeled>
                  <button
                    onClick={handleSaveName}
                    disabled={savingName}
                    style={{ fontSize: '12px', padding: '6px 12px', border: `1px solid ${GOLD}`, borderRadius: '6px', color: GOLD, background: 'transparent', cursor: savingName ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setEditingName(false)}
                    disabled={savingName}
                    style={{ fontSize: '12px', padding: '6px 12px', border: '1px solid #ddd', borderRadius: '6px', color: TEXT_MUTED, background: 'transparent', cursor: savingName ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <h1 style={p.name}>{employee.name}</h1>
                  {canEditProfileFields && (
                    <button
                      onClick={() => {
                        setEditFirstName(employeeRaw?.first_name || '');
                        setEditLastName(employeeRaw?.last_name || '');
                        setEditingName(true);
                      }}
                      title="Correct a typo in this employee's name (display only - does not change login email)"
                      style={{ fontSize: '11px', padding: '3px 9px', border: `1px solid ${GOLD}`, borderRadius: '4px', color: GOLD, background: 'transparent', cursor: 'pointer', whiteSpace: 'nowrap' }}
                    >
                      Edit
                    </button>
                  )}
                </div>
              )}
              {/* CAREERTL: the job title is now editable (it had no update path at all before),
                  and a change is recorded on the timeline below. coo/admin only. */}
              {editingJobTitle ? (
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap', marginTop: '4px' }}>
                  <Labeled label={dashT('profileFields.job_title')} dark={isDarkMode}><input
                    type="text"
                    value={editJobTitle}
                    onChange={e => setEditJobTitle(e.target.value)}
                    placeholder="Job title"
                    style={{ fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px', padding: '6px 10px', width: '220px', outline: 'none', color: NAVY_SHELL, background: '#fff' }}
                  /></Labeled>
                  <button
                    onClick={handleSaveJobTitle}
                    disabled={savingJobTitle}
                    style={{ fontSize: '12px', padding: '6px 12px', border: `1px solid ${GOLD}`, borderRadius: '6px', color: GOLD, background: 'transparent', cursor: savingJobTitle ? 'wait' : 'pointer' }}
                  >Save</button>
                  <button
                    onClick={() => setEditingJobTitle(false)}
                    disabled={savingJobTitle}
                    style={{ fontSize: '12px', padding: '6px 12px', border: '1px solid #ddd', borderRadius: '6px', color: TEXT_MUTED, background: 'transparent', cursor: savingJobTitle ? 'wait' : 'pointer' }}
                  >Cancel</button>
                </div>
              ) : (
                (employee.job_title || employee.company || canEditProfileFields) && (
                  <p style={{ ...p.roleText, display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <span>
                      {employee.job_title}{employee.job_title && employee.company ? ' · ' : ''}{employee.company}
                    </span>
                    {canEditProfileFields && (
                      <button
                        onClick={() => { setEditJobTitle(employeeRaw?.job_title || ''); setEditingJobTitle(true); }}
                        title="Change this employee's job title (recorded on their career timeline)"
                        style={{ fontSize: '11px', padding: '3px 9px', border: `1px solid ${GOLD}`, borderRadius: '4px', color: GOLD, background: 'transparent', cursor: 'pointer', whiteSpace: 'nowrap' }}
                      >
                        {employee.job_title ? 'Edit title' : 'Set title'}
                      </button>
                    )}
                  </p>
                )
              )}
              {/* EMAILFIX: company_email is the LOGIN identity, so it is shown here with a
                  coo/admin-only correction control and an explicit warning. Distinct from the
                  name Edit above, which is display-only and untouched by this. */}
              {employeeRaw?.company_email && (
                editingCompanyEmail ? (
                  <div style={{ marginTop: '6px' }}>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                      <Labeled label={dashT('profileFields.company_email')} dark={isDarkMode}><input
                        type="email"
                        value={editCompanyEmail}
                        onChange={e => setEditCompanyEmail(e.target.value.toLowerCase())}
                        placeholder="company email"
                        style={{ fontSize: '13px', border: '1px solid #ddd', borderRadius: '6px', padding: '6px 10px', width: '260px', outline: 'none', color: NAVY_SHELL, background: '#fff' }}
                      /></Labeled>
                      <button
                        onClick={handleSaveCompanyEmail}
                        disabled={savingCompanyEmail}
                        style={{ fontSize: '12px', padding: '6px 12px', border: `1px solid ${GOLD}`, borderRadius: '6px', color: GOLD, background: 'transparent', cursor: savingCompanyEmail ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}
                      >
                        Save
                      </button>
                      <button
                        onClick={() => setEditingCompanyEmail(false)}
                        disabled={savingCompanyEmail}
                        style={{ fontSize: '12px', padding: '6px 12px', border: '1px solid #ddd', borderRadius: '6px', color: TEXT_MUTED, background: 'transparent', cursor: savingCompanyEmail ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}
                      >
                        Cancel
                      </button>
                    </div>
                    <p style={{ fontSize: '11px', color: '#92400E', margin: '6px 0 0 0', maxWidth: '460px', lineHeight: 1.5 }}>
                      This is the address they sign in with. Changing it updates their login
                      identity and any delegate access pointing at the old address.
                    </p>
                  </div>
                ) : (
                  <div style={{ marginTop: '6px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '12px', color: TEXT_MUTED }}>{employeeRaw.company_email}</span>
                    {canEditProfileFields && (
                      <button
                        onClick={() => {
                          setEditCompanyEmail(employeeRaw?.company_email || '');
                          setEditingCompanyEmail(true);
                        }}
                        title="Correct this employee's company email - this is also their login identity"
                        style={{ fontSize: '11px', padding: '3px 9px', border: `1px solid ${GOLD}`, borderRadius: '4px', color: GOLD, background: 'transparent', cursor: 'pointer', whiteSpace: 'nowrap' }}
                      >
                        Edit login email
                      </button>
                    )}
                  </div>
                )
              )}
              <div style={{ marginTop: '6px', display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                {employee.requires_visa
                  ? <span style={p.visaBadge}>Visa Required</span>
                  : <span style={{ fontSize: '12px', color: TEXT_MUTED }}>No visa required</span>
                }
                {employeeRaw?.account_role === 'ceo' && (
                  <span style={{ display: 'inline-block', backgroundColor: NAVY_SHELL, color: GOLD, fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px' }}>CEO</span>
                )}
                {newStarterForm?.background_check_in_progress && !newStarterForm?.background_check_global_done && (
                  <span style={{ display: 'inline-block', backgroundColor: '#FEF3C7', color: '#92400E', border: '1px solid #F59E0B', fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px' }}>
                    Background check pending
                  </span>
                )}
              </div>
            </div>
            {/* CARDHEAD: once the row wraps, this cluster is alone on its own line, so holding it
                at justifyContent: flex-end would leave the buttons floating far from the name they
                act on. When narrow it therefore aligns left under the name block and takes the
                full line (flex: 1 1 100%), which is also what guarantees it can never sit beside
                the name and overlap it. At desktop width the original right-aligned,
                non-growing behaviour is unchanged. */}
            <div style={{
              display: 'flex', gap: '8px', flexWrap: 'wrap',
              ...(isNarrow
                ? { flex: '1 1 100%', justifyContent: 'flex-start' }
                : { flexShrink: 0, justifyContent: 'flex-end' }),
            }}>
              {/* CAREERTL: offered for ANY current intern, not only one whose end date has
                  passed - a conversion is typically decided before the internship runs out.
                  Deliberately placed before the offboarding buttons so "keep them" reads as the
                  first option rather than an afterthought. */}
              {employee?.employee_type === 'intern' && (role === 'coo' || role === 'admin' || role === 'hr') && !isArchived && (
                <button
                  style={{ ...p.leaverBtn, backgroundColor: '#2D4A3E' }}
                  onClick={openConvertForm}
                  disabled={converting}
                  title="Convert this intern to a permanent employee"
                >
                  {converting ? 'Converting...' : 'Convert to permanent employee'}
                </button>
              )}
              {/* HRLEAVER: hr starts a leaver process exactly as the COO does. */}
              {internEnded && (role === 'coo' || role === 'hr') && (
                <button
                  style={{ ...p.leaverBtn, borderColor: GOLD, color: GOLD }}
                  onClick={() => setLeaverSection(1)}
                  title="This internship has ended - start the offboarding process"
                >
                  Internship ended: start offboarding
                </button>
              )}
              {!leaverStarted && (role === 'coo' || role === 'hr') && !isArchived && !internEnded && (
                <button
                  style={p.leaverBtn}
                  onClick={() => setLeaverSection(1)}
                  title={leaverDraft ? 'A Section 1 draft is saved - click to resume it' : undefined}
                >
                  {/* LEAVERDRAFT: a saved draft must read as started at a glance, not as a
                      fresh state. Same click either way - Section 1 loads the draft itself. */}
                  {leaverDraft ? 'Resume Leaver Process (draft saved)' : 'Start Leaver Process'}
                </button>
              )}
              {hasCooSettings && (
                <button
                  onClick={handleTogglePermission}
                  disabled={togglingPermission}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '8px',
                    backgroundColor: 'transparent',
                    border: `1px solid ${canSubmitNewStarters ? '#2D4A3E' : '#ddd'}`,
                    borderRadius: '6px', padding: '7px 14px', fontSize: '12px',
                    cursor: togglingPermission ? 'wait' : 'pointer',
                    color: canSubmitNewStarters ? '#2D4A3E' : '#888',
                    whiteSpace: 'nowrap',
                  }}
                >
                  <span style={{
                    display: 'inline-block', width: '28px', height: '14px',
                    borderRadius: '7px', position: 'relative',
                    backgroundColor: canSubmitNewStarters ? '#2D4A3E' : '#ddd',
                    transition: 'background 0.2s', flexShrink: 0,
                  }}>
                    <span style={{
                      position: 'absolute', top: '2px',
                      left: canSubmitNewStarters ? '16px' : '2px',
                      width: '10px', height: '10px', borderRadius: '50%',
                      backgroundColor: '#fff', transition: 'left 0.2s',
                    }} />
                  </span>
                  Can submit new starters
                </button>
              )}
              {/* HRLEAVER: hr archives exactly as the COO does (same button, same confirmation).
                  Unarchive stays the COO's: the server refuses it to hr, so hr is not offered it. */}
              {(role === 'coo' || (role === 'hr' && !isArchived)) && (
                <button
                  style={isArchived ? p.unarchiveBtn : p.archiveBtn}
                  onClick={handleArchiveToggle}
                  disabled={archiving}
                >
                  {archiving ? 'Processing...' : (isArchived ? 'Unarchive Employee' : 'Archive Employee')}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* ── CAREERTL: career timeline ── */}
        <div style={p.card}>
          <p style={p.sectionTitle}>Career timeline</p>
          <CareerTimeline
            entries={roleHistory}
            dark={isDarkMode}
            loading={roleHistoryLoading}
            error={roleHistoryError}
            onRetry={loadRoleHistory}
          />
        </div>

        {/* ── Details card ── */}
        <div style={p.card} ref={niRef}>
          <p style={p.sectionTitle}>Details</p>
          <div style={{ ...p.detailsGrid, gridTemplateColumns: gridCols(isNarrow, 'repeat(3, 1fr)') }}>
            {employee.email    && <div style={p.field}><span style={p.fieldLabel}>Email</span>     <span style={p.fieldValue}>{employee.email}</span></div>}
            {employee.start_date && <div style={p.field}><span style={p.fieldLabel}>Start date</span><span style={p.fieldValue}>{employee.start_date}</span></div>}
            {employee.location && <div style={p.field}><span style={p.fieldLabel}>Location</span>  <span style={p.fieldValue}>{employee.location}</span></div>}
            {employee.mobile   && <div style={p.field}><span style={p.fieldLabel}>Mobile</span>    <span style={p.fieldValue}>{employee.mobile}</span></div>}
            {employee.gender   && <div style={p.field}><span style={p.fieldLabel}>Gender</span>    <span style={p.fieldValue}>{employee.gender}</span></div>}
            {employee.department && <div style={p.field}><span style={p.fieldLabel}>Department</span><span style={p.fieldValue}>{employee.department}</span></div>}
            {employeeRaw?.function_team && <div style={p.field}><span style={p.fieldLabel}>{dashT('hrProfile.team')}</span><span style={p.fieldValue} data-testid="profile-team">{employeeRaw.function_team}</span></div>}
            {employee.employee_type && <div style={p.field}><span style={p.fieldLabel}>Employment type</span><span style={p.fieldValue}>{employee.employee_type.charAt(0).toUpperCase() + employee.employee_type.slice(1)}</span></div>}
            {employee.employee_type === 'intern' && employee.intern_term && <div style={p.field}><span style={p.fieldLabel}>Internship term</span><span style={p.fieldValue}>{employee.intern_term === 'short_term' ? 'Short-term' : 'Long-term'}</span></div>}
            {employee.employee_type === 'intern' && employee.intern_term === 'long_term' && employee.intern_duration && <div style={p.field}><span style={p.fieldLabel}>Expected duration</span><span style={p.fieldValue}>{employee.intern_duration}</span></div>}
            {employee.employee_type === 'intern' && employee.internship_end_date && <div style={p.field}><span style={p.fieldLabel}>Internship end date</span><span style={p.fieldValue}>{employee.internship_end_date}</span></div>}
            {employeeRaw?.fca_classification && <div style={p.field}><span style={p.fieldLabel}>FCA Classification</span><span style={p.fieldValue}>{employeeRaw.fca_classification}</span></div>}
            {(managerName || employeeRaw?.manager_name_unmatched) && (
              <div style={p.field}>
                <span style={p.fieldLabel}>Manager</span>
                <span style={p.fieldValue}>
                  {managerName || employeeRaw.manager_name_unmatched}
                  {!managerName && employeeRaw?.manager_name_unmatched && <span style={{ fontSize: '11px', color: '#aaa', marginLeft: '6px' }}>(unmatched)</span>}
                </span>
              </div>
            )}
            {(newStarterForm?.ni_number || employeeRaw?.ni_number) && (
              <div style={p.field}>
                <span style={p.fieldLabel}>NI / National ID</span>
                <span style={p.fieldValue}>{newStarterForm?.ni_number || employeeRaw?.ni_number}</span>
              </div>
            )}
          </div>
          {hasCooSettings && (
            <>
            <div style={{ marginTop: '12px', borderTop: '0.5px solid rgba(26,39,68,0.08)', paddingTop: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={p.fieldLabel}>Requires Visa Sponsorship</span>
              <button
                onClick={handleToggleVisa}
                disabled={togglingVisa}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  backgroundColor: 'transparent',
                  border: `1px solid ${requiresVisa ? GOLD : '#ddd'}`,
                  borderRadius: '6px', padding: '7px 14px', fontSize: '12px',
                  cursor: togglingVisa ? 'wait' : 'pointer',
                  color: requiresVisa ? GOLD : '#888',
                  whiteSpace: 'nowrap',
                }}
              >
                <span style={{
                  display: 'inline-block', width: '28px', height: '14px',
                  borderRadius: '7px', position: 'relative',
                  backgroundColor: requiresVisa ? GOLD : '#ddd',
                  transition: 'background 0.2s', flexShrink: 0,
                }}>
                  <span style={{
                    position: 'absolute', top: '2px',
                    left: requiresVisa ? '16px' : '2px',
                    width: '10px', height: '10px', borderRadius: '50%',
                    backgroundColor: '#fff', transition: 'left 0.2s',
                  }} />
                </span>
                {requiresVisa ? 'Visa required' : 'No visa required'}
              </button>
            </div>
            <div style={{ marginTop: '8px', borderTop: '0.5px solid rgba(26,39,68,0.08)', paddingTop: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={p.fieldLabel}>Residence Permit / Settled Status</span>
              <button
                onClick={handleTogglePermit}
                disabled={togglingPermit}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  backgroundColor: 'transparent',
                  border: `1px solid ${hasResidencePermit ? GOLD : '#ddd'}`,
                  borderRadius: '6px', padding: '7px 14px', fontSize: '12px',
                  cursor: togglingPermit ? 'wait' : 'pointer',
                  color: hasResidencePermit ? GOLD : '#888',
                  whiteSpace: 'nowrap',
                }}
              >
                <span style={{
                  display: 'inline-block', width: '28px', height: '14px',
                  borderRadius: '7px', position: 'relative',
                  backgroundColor: hasResidencePermit ? GOLD : '#ddd',
                  transition: 'background 0.2s', flexShrink: 0,
                }}>
                  <span style={{
                    position: 'absolute', top: '2px',
                    left: hasResidencePermit ? '16px' : '2px',
                    width: '10px', height: '10px', borderRadius: '50%',
                    backgroundColor: '#fff', transition: 'left 0.2s',
                  }} />
                </span>
                {hasResidencePermit ? 'Has permit' : 'No permit'}
              </button>
            </div>
            <div style={{ marginTop: '8px', borderTop: '0.5px solid rgba(26,39,68,0.08)', paddingTop: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={p.fieldLabel}>Frequent Traveller</span>
              <button
                onClick={handleToggleTraveller}
                disabled={togglingTraveller}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  backgroundColor: 'transparent',
                  border: `1px solid ${isFrequentTraveller ? GOLD : '#ddd'}`,
                  borderRadius: '6px', padding: '7px 14px', fontSize: '12px',
                  cursor: togglingTraveller ? 'wait' : 'pointer',
                  color: isFrequentTraveller ? GOLD : '#888',
                  whiteSpace: 'nowrap',
                }}
              >
                <span style={{
                  display: 'inline-block', width: '28px', height: '14px',
                  borderRadius: '7px', position: 'relative',
                  backgroundColor: isFrequentTraveller ? GOLD : '#ddd',
                  transition: 'background 0.2s', flexShrink: 0,
                }}>
                  <span style={{
                    position: 'absolute', top: '2px',
                    left: isFrequentTraveller ? '16px' : '2px',
                    width: '10px', height: '10px', borderRadius: '50%',
                    backgroundColor: '#fff', transition: 'left 0.2s',
                  }} />
                </span>
                {isFrequentTraveller ? 'Frequent traveller' : 'Not a traveller'}
              </button>
            </div>
            <div style={{ marginTop: '8px', borderTop: '0.5px solid rgba(26,39,68,0.08)', paddingTop: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
              <span style={p.fieldLabel}>Secondary Entity</span>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <select
                  value={secondaryEntity}
                  onChange={e => setSecondaryEntity(e.target.value)}
                  style={{ fontSize: '12px', border: '1px solid #ddd', borderRadius: '6px', padding: '6px 10px', width: '200px', outline: 'none', color: NAVY_SHELL, background: '#fff' }}
                >
                  <option value="">None</option>
                  {SECONDARY_ENTITY_OPTIONS.map(e => <option key={e} value={e}>{e}</option>)}
                </select>
                <button
                  onClick={handleSaveSecondaryEntity}
                  disabled={savingSecondaryEntity}
                  style={{ fontSize: '12px', padding: '6px 12px', border: `1px solid ${GOLD}`, borderRadius: '6px', color: GOLD, background: 'transparent', cursor: savingSecondaryEntity ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}
                >
                  Save
                </button>
              </div>
            </div>
            {(employeeRaw?.employee_type === 'intern' || employee.employee_type === 'intern') && (
              <div style={{ marginTop: '8px', borderTop: '0.5px solid rgba(26,39,68,0.08)', paddingTop: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                <span style={p.fieldLabel}>Internship end date</span>
                <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                  <input
                    type="date"
                    value={internshipEndDate}
                    onChange={e => setInternshipEndDate(e.target.value)}
                    style={{ fontSize: '12px', border: '1px solid #ddd', borderRadius: '6px', padding: '6px 10px', width: '200px', outline: 'none', color: NAVY_SHELL, background: '#fff' }}
                  />
                  <button
                    onClick={handleSaveInternshipEnd}
                    disabled={savingInternshipEnd}
                    style={{ fontSize: '12px', padding: '6px 12px', border: `1px solid ${GOLD}`, borderRadius: '6px', color: GOLD, background: 'transparent', cursor: savingInternshipEnd ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}
                  >
                    Save
                  </button>
                </div>
              </div>
            )}
            {/* RESENDWELCOME: personal_email is the delivery address for the MANUAL "Resend
                welcome email" button, which is deliberately unchanged and still resolves
                personal-first. WELCOMEDELAY narrowed this: the AUTOMATIC welcome no longer uses
                this address at all, going to the company mailbox only. Editable here so it can
                still be set retroactively. */}
            <div style={{ marginTop: '8px', borderTop: '0.5px solid rgba(26,39,68,0.08)', paddingTop: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
              <span style={p.fieldLabel}>Personal email</span>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <input
                  type="text"
                  value={personalEmail}
                  onChange={e => setPersonalEmail(e.target.value)}
                  placeholder="personal@email.com"
                  title="Payslip address. Also used by the manual Resend welcome email button; the automatic welcome goes to the company mailbox only."
                  style={{ fontSize: '12px', border: '1px solid #ddd', borderRadius: '6px', padding: '6px 10px', width: '200px', outline: 'none', color: NAVY_SHELL, background: '#fff' }}
                />
                <button
                  onClick={handleSavePersonalEmail}
                  disabled={savingPersonalEmail}
                  style={{ fontSize: '12px', padding: '6px 12px', border: `1px solid ${GOLD}`, borderRadius: '6px', color: GOLD, background: 'transparent', cursor: savingPersonalEmail ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}
                >
                  Save
                </button>
              </div>
            </div>
            <div style={{ marginTop: '8px', borderTop: '0.5px solid rgba(26,39,68,0.08)', paddingTop: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
              <span style={p.fieldLabel}>Delegate email</span>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <input
                  type="text"
                  value={delegateEmail}
                  onChange={e => setDelegateEmail(e.target.value)}
                  placeholder="email@enkocapital.com"
                  style={{ fontSize: '12px', border: '1px solid #ddd', borderRadius: '6px', padding: '6px 10px', width: '200px', outline: 'none', color: NAVY_SHELL, background: '#fff' }}
                />
                <button
                  onClick={handleSaveDelegateEmail}
                  disabled={savingDelegate}
                  style={{ fontSize: '12px', padding: '6px 12px', border: `1px solid ${GOLD}`, borderRadius: '6px', color: GOLD, background: 'transparent', cursor: savingDelegate ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}
                >
                  Save
                </button>
                {delegateEmail && (
                  <button
                    onClick={handleResendDelegateNotification}
                    disabled={resendingDelegate}
                    style={{ fontSize: '12px', padding: '6px 12px', border: '1px solid #ddd', borderRadius: '6px', color: '#888', background: 'transparent', cursor: resendingDelegate ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}
                  >
                    {resendingDelegate ? 'Sending…' : 'Resend notification'}
                  </button>
                )}
              </div>
            </div>
            </>
          )}
          {/* CLMANAGER / MGRPICKERFIX: the line manager control used to live INSIDE the
              role === 'coo' fragment above, so canEditManager's Country Lead arm was structurally
              dead - the outer role === 'coo' check refused the whole fragment before
              canEditManager was ever reached. That is the deeper cause behind "the picker shows a
              search box that can never return a result" for a Country Lead: it was not merely an
              unpopulated fetch, the control never rendered at all. Moved out to its own sibling,
              gated on canEditManager alone.

              MGRNOADMIN: that move also handed the control to ADMIN, which was never asked for.
              canEditManager no longer admits admin (see its definition above), so this renders for
              the COO and a Country Lead only. coo is unaffected and always has been.
              MGRPROFILE SUPERSESSION (2026-10-28): the COO, admin and HR change the manager here, and only
              here (the list no longer does), so admin has the field again. */}
          {canEditManager && (
            <div style={{ marginTop: '12px', borderTop: '0.5px solid rgba(26,39,68,0.08)', paddingTop: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }} ref={managerFieldRef}>
              <span style={p.fieldLabel}>{managerFieldFull ? dashT('mgrProfile.label') : 'Manager'}</span>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <div style={{ position: 'relative' }}>
                  <input
                    type="text"
                    value={managerSearchName || managerSearch}
                    placeholder={managerFieldFull ? dashT('mgrProfile.searchPlaceholder') : 'Type a name to search…'}
                    aria-label={managerFieldFull ? dashT('mgrProfile.label') : undefined}
                    onChange={e => {
                      const val = e.target.value;
                      setManagerSearch(val);
                      setManagerSearchName('');
                      setPendingManagerId('');
                      setShowManagerList(true);
                      setManagerError(null);
                    }}
                    onFocus={() => { if (!managerSearchName) setShowManagerList(true); }}
                    style={{ fontSize: '12px', border: '1px solid #ddd', borderRadius: '6px', padding: '6px 10px', width: '200px', outline: 'none', color: NAVY_SHELL, background: '#fff' }}
                  />
                  {managerSearchName && (
                    <button
                      style={{ position: 'absolute', right: '8px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: '13px', lineHeight: 1 }}
                      onClick={() => { setManagerSearchName(''); setManagerSearch(''); setPendingManagerId(''); setShowManagerList(false); }}
                    >✕</button>
                  )}
                  {/* DARKFIX: the manager-search dropdown was hardcoded white with NAVY_FORM
                      text, so in dark mode it rendered as a white panel over a dark page - a
                      jarring light slab. Uses the same surface/border values mkP's own `card`
                      token uses for each mode. */}
                  {showManagerList && filteredManagers.length > 0 && (
                    <div style={{ position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0, backgroundColor: isDarkMode ? NAVY_SHELL : '#fff', border: `1px solid ${isDarkMode ? 'rgba(201,168,76,0.15)' : '#ddd'}`, borderRadius: '6px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 50, maxHeight: '220px', overflowY: 'auto' }}>
                      {filteredManagers.map(emp => (
                        <div
                          key={emp.id}
                          style={{ padding: '9px 12px', cursor: 'pointer', fontSize: '13px', color: isDarkMode ? TEXT_DARK_MODE : NAVY_FORM, backgroundColor: isDarkMode ? NAVY_SHELL : '#fff', display: 'flex', alignItems: 'baseline', borderBottom: `0.5px solid ${isDarkMode ? 'rgba(201,168,76,0.1)' : '#f4f4f4'}`, transition: 'background 0.1s' }}
                          onMouseEnter={e => { e.currentTarget.style.backgroundColor = isDarkMode ? '#243058' : '#FFF8EC'; e.currentTarget.style.color = isDarkMode ? GOLD : '#8B6914'; }}
                          onMouseLeave={e => { e.currentTarget.style.backgroundColor = isDarkMode ? NAVY_SHELL : '#fff'; e.currentTarget.style.color = isDarkMode ? TEXT_DARK_MODE : NAVY_FORM; }}
                          onMouseDown={e => {
                            e.preventDefault();
                            setManagerSearchName(`${emp.first_name} ${emp.last_name}`);
                            setManagerSearch('');
                            setPendingManagerId(emp.id);
                            setShowManagerList(false);
                          }}
                        >
                          <span style={{ fontWeight: 500 }}>{emp.first_name} {emp.last_name}</span>
                          <span style={{ fontSize: '11px', color: '#aaa', marginLeft: '8px' }}>{emp.job_title || emp.department || ''}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {/* MGRPICKERFIX: two DISTINCT states, never collapsed into one another. The fetch
                      failing (managerListUnavailable) means the search cannot work AT ALL, however
                      it is typed - shown regardless of what is typed, same wording/colour as
                      AddEmployee.js's own MGRSEARCH notice for the same failure. A genuinely empty
                      RESULT only means this particular name matched nobody in a fetch that
                      succeeded - shown only once a search has actually been typed (matching
                      filteredManagers' own "< 1 char -> []" rule, so it never appears before the
                      user has searched for anything) and never at the same time as the failure
                      message, since managerListUnavailable is checked first. */}
                  {showManagerList && managerListUnavailable && (
                    <p style={{ fontSize: '12px', color: '#8a6d00', margin: '6px 0 0' }}>
                      {managerFieldFull ? dashT('mgrProfile.unavailable')
                        : 'Manager search is temporarily unavailable. You can set the manager later.'}
                    </p>
                  )}
                  {showManagerList && !managerListUnavailable && managerSearch.trim().length > 0 && filteredManagers.length === 0 && (
                    <p style={{ fontSize: '12px', color: isDarkMode ? TEXT_MUTED : '#888', margin: '6px 0 0' }}>
                      {managerFieldFull ? dashT('mgrProfile.noMatch', { query: managerSearch.trim() })
                        : `No employee matches "${managerSearch.trim()}".`}
                    </p>
                  )}
                  {/* MGRPROFILE: a refusal, translated, beside the field. */}
                  {managerFieldFull && managerError && (
                    <p role="alert" style={{ fontSize: '12px', color: '#A63A3A', margin: '6px 0 0', maxWidth: '220px' }}>
                      {managerError}
                    </p>
                  )}
                </div>
                <button
                  onClick={() => handleSaveManager()}
                  disabled={savingManager}
                  style={{ fontSize: '12px', padding: '6px 12px', border: `1px solid ${GOLD}`, borderRadius: '6px', color: GOLD, background: 'transparent', cursor: savingManager ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}
                >
                  {managerFieldFull ? dashT('mgrProfile.save') : 'Save'}
                </button>
                {/* MGRPROFILE: the clear way to remove the manager. */}
                {managerFieldFull && employeeRaw?.manager_id && (
                  <button
                    type="button"
                    onClick={() => handleSaveManager(true)}
                    disabled={savingManager}
                    style={{ fontSize: '12px', padding: '6px 12px', border: '1px solid #ddd', borderRadius: '6px', color: '#A63A3A', background: 'transparent', cursor: savingManager ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}
                  >
                    {dashT('mgrProfile.remove')}
                  </button>
                )}
              </div>
            </div>
          )}
          {/* HRPROFILE: the team, edited by hr and the COO. Free text with the teams already in use
              as suggestions; an empty value clears it. */}
          {canEditTeam && (
            <div style={{ marginTop: '12px', borderTop: '0.5px solid rgba(26,39,68,0.08)', paddingTop: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
              <label style={p.fieldLabel} htmlFor={`${a11yId}-team`}>{dashT('hrProfile.team')}</label>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <input
                  id={`${a11yId}-team`}
                  type="text"
                  list={`${a11yId}-team-options`}
                  value={team}
                  maxLength={100}
                  onChange={e => setTeam(e.target.value)}
                  placeholder={dashT('hrProfile.teamPlaceholder')}
                  style={{ fontSize: '12px', border: '1px solid #ddd', borderRadius: '6px', padding: '6px 10px', width: '200px', outline: 'none', color: NAVY_SHELL, background: '#fff' }}
                />
                <datalist id={`${a11yId}-team-options`}>
                  {teamOptions.map(o => <option key={o} value={o} />)}
                </datalist>
                <button
                  onClick={handleSaveTeam}
                  disabled={savingTeam}
                  style={{ fontSize: '12px', padding: '6px 12px', border: `1px solid ${GOLD}`, borderRadius: '6px', color: GOLD, background: 'transparent', cursor: savingTeam ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}
                >
                  {dashT('hrProfile.save')}
                </button>
              </div>
            </div>
          )}
          {/* CONTRACTEXEMPT2 (confirmed COO decision): independently gated on coo OR admin (not
              folded into the role === 'coo' block above, which is narrower and covers unrelated
              visa/permit/traveller/secondary-entity/delegate controls out of this task's
              scope) - the route itself (PATCH .../contract-exempt) is coo/admin-gated
              server-side, this is the matching UI visibility. */}
          {(role === 'coo' || role === 'admin' || role === 'hr') && (
            <div style={{ marginTop: '12px', borderTop: '0.5px solid rgba(26,39,68,0.08)', paddingTop: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={p.fieldLabel}>Contract Upload Exemption</span>
              <button
                onClick={handleToggleContractExempt}
                disabled={togglingContractExempt}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  backgroundColor: 'transparent',
                  border: `1px solid ${contractExempt ? GOLD : '#ddd'}`,
                  borderRadius: '6px', padding: '7px 14px', fontSize: '12px',
                  cursor: togglingContractExempt ? 'wait' : 'pointer',
                  color: contractExempt ? GOLD : '#888',
                  whiteSpace: 'nowrap',
                }}
              >
                <span style={{
                  display: 'inline-block', width: '28px', height: '14px',
                  borderRadius: '7px', position: 'relative',
                  backgroundColor: contractExempt ? GOLD : '#ddd',
                  transition: 'background 0.2s', flexShrink: 0,
                }}>
                  <span style={{
                    position: 'absolute', top: '2px',
                    left: contractExempt ? '16px' : '2px',
                    width: '10px', height: '10px', borderRadius: '50%',
                    backgroundColor: '#fff', transition: 'left 0.2s',
                  }} />
                </span>
                {contractExempt ? 'Exempt from contract upload' : 'Not exempt'}
              </button>
            </div>
          )}
        </div>

        {/* ── Leaver card ── */}
        {/* LEAVERDRAFT: also rendered for a saved draft (row exists, S1 not submitted), with
            Status "Draft" and S1 naturally showing Pending - same visual pattern as in-progress. */}
        {(leaverStarted || leaverDraft) && (
          <div style={{ ...p.card, borderLeft: '3px solid #A63A3A' }}>
            <p style={{ ...p.sectionTitle, opacity: 1, color: '#A63A3A' }}>Leaver Process</p>
            <div style={p.statusGrid}>
              <div style={p.statusItem}>
                <span style={p.fieldLabel}>Status</span>
                <span style={{ ...p.fieldValue, color: leaverComplete ? '#2D4A3E' : '#A63A3A' }}>
                  {leaverComplete ? '✓ Complete' : leaverDraft ? 'Draft' : 'In progress'}
                </span>
              </div>
              <div style={p.statusItem}>
                <span style={p.fieldLabel}>Last Working Date</span>
                <span style={p.fieldValue}>
                  {leaverForm?.last_working_date ? toDisplayDate(leaverForm.last_working_date).toLocaleDateString() : '-'}
                </span>
              </div>
              <div style={p.statusItem}><span style={p.fieldLabel}>Manager</span>{nsStatus(leaverForm?.section1_completed)}</div>
              <div style={p.statusItem}><span style={p.fieldLabel}>IT</span>{nsStatus(leaverForm?.section2_completed)}</div>
              <div style={p.statusItem}><span style={p.fieldLabel}>Payroll and Benefits</span>{nsStatus(leaverForm?.section3_completed)}</div>
            </div>
            {leaverForm?.section1_completed && !leaverComplete && (
              <div style={{ display: 'flex', gap: '8px', marginTop: '14px', flexWrap: 'wrap' }}>
                {/* HRLEAVER: the IT section stays with the COO, as new starter Section 2 does. */}
                {!leaverForm?.section2_completed && role !== 'hr' && (
                  <button style={p.fillBtn} onClick={() => setLeaverSection(2)}>Fill IT Section</button>
                )}
                {!leaverForm?.section3_completed && (
                  <button style={p.fillBtn} onClick={() => setLeaverSection(3)}>Fill Payroll & Benefits</button>
                )}
              </div>
            )}
          </div>
        )}

        {/* ── New Starter Onboarding card ── */}
        <div style={p.card} ref={contactRef}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <p style={{ ...p.sectionTitle, margin: 0 }}>New Starter Onboarding</p>
            <span style={{ fontSize: '12px', fontWeight: 700, color: GOLD }}>{pct}%</span>
          </div>
          <div style={{ height: '4px', backgroundColor: '#edecea', borderRadius: '99px', overflow: 'hidden', marginBottom: '16px' }}>
            <div style={{ height: '4px', backgroundColor: GOLD, borderRadius: '99px', width: `${pct}%`, transition: 'width 0.3s ease' }} />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
            {visibleNsSections.map(sec => (
              <div key={sec.key} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', borderRadius: '6px', backgroundColor: '#FAFAF8' }}>
                {/* S1ONEROW: three states, not two. A half-done Section 1 must not read as
                    complete and must not read as untouched either. The partial badge uses a
                    HALF-BLOCK glyph rather than the done tick or the pending dash, so the three
                    states are distinguishable by SHAPE and not by colour alone. */}
                <div style={{ width: '28px', height: '28px', borderRadius: '50%', backgroundColor: sec.done ? '#E8F3EE' : '#FFF8EC', color: sec.done ? '#2D4A3E' : GOLD, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', fontWeight: '700', flexShrink: 0 }}>
                  {sec.done ? '✓' : sec.partial ? '◐' : '-'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ fontSize: '13px', fontWeight: 500, color: NAVY_SHELL, margin: '0 0 2px 0' }}>{sec.label}</p>
                  <p style={{ fontSize: '11px', color: TEXT_MUTED, margin: 0 }}>
                    {sec.done
                      ? `Completed${sec.by ? ` by ${sec.by}` : ''}${sec.at ? ` on ${toDisplayDate(sec.at).toLocaleDateString()}` : ''}`
                      : sec.partial
                        ? (sec.partialLabel || 'Partly done')
                        : 'Pending'}
                  </p>
                </div>
                {!sec.done && sec.fillSection && (
                  <button style={p.fillBtn} onClick={() => setShowNsSection(sec.fillSection)}>
                    Fill {sec.label}
                  </button>
                )}
                {/* MARKPREEXIST: existing/migrated employees only - IT setup / payroll were
                    handled manually pre-portal, so the COO can mark the section done without a
                    real submission; the backend also sets the email-sent flag so the real
                    IT/Payroll notification can never fire for them. New starters (not
                    migration_confirmed) never see this button. */}
                {!sec.done && sec.fillSection && employeeRaw?.migration_confirmed && ['coo', 'admin', 'hr'].includes(role) && (
                  <button
                    // MARKPREEXISTBLUE (confirmed decision by Marie-Lynne, replacing MARKPREEXISTVIS's
                    // gold): a light blue fill with a darker blue label and border, matching the
                    // workflow diagram nodes' palette rather than this codebase's GOLD accent. Still
                    // visually distinct from the solid navy "Fill {label}" button beside it - light
                    // fill + outline reads as secondary next to a solid dark button, the same
                    // primary/secondary contrast GOLD used to provide, just in a different hue.
                    // p.fillBtn's border:'none' and backgroundColor are both overridden here; its
                    // border-less base is why a border is added explicitly rather than inherited.
                    // Logic (onClick/mark-preexisting) unchanged.
                    style={{ ...p.fillBtn, backgroundColor: '#BDD7EE', color: '#1F4E79',
                            border: '1px solid #4A83B8', fontWeight: 700 }}
                    title="Marks this section as handled before the portal existed - no email is sent"
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/new-starter/${employeeId}/mark-preexisting`, { sections: [sec.fillSection] });
                        showToast(`${sec.label} marked as already done`, 'success');
                        loadData();
                      } catch (err) {
                        showToast(err.response?.data?.detail || 'Could not mark section', 'error');
                      }
                    }}
                  >
                    Mark as already done
                  </button>
                )}
              </div>
            ))}
          </div>

          {newStarterForm?.section1_completed && (
            <div style={p.dataGrid}>
              {(() => {
                const parts = [
                  employeeRaw?.address_line1,
                  employeeRaw?.address_line2,
                  employeeRaw?.city,
                  employeeRaw?.state_county,
                  employeeRaw?.postcode,
                  employeeRaw?.country_of_residence,
                ].filter(Boolean);
                return parts.length > 0
                  ? <div style={p.field}><span style={p.fieldLabel}>Address</span><span style={p.fieldValue}>{parts.join(', ')}</span></div>
                  : null;
              })()}
              {newStarterForm.personal_email && <div style={p.field}><span style={p.fieldLabel}>Personal Email</span><span style={p.fieldValue}>{newStarterForm.personal_email}</span></div>}
              {newStarterForm.personal_mobile && <div style={p.field}><span style={p.fieldLabel}>Mobile</span><span style={p.fieldValue}>{newStarterForm.personal_mobile}</span></div>}
              {newStarterForm.emergency_contact_name && <div style={p.field}><span style={p.fieldLabel}>Emergency Contact</span><span style={p.fieldValue}>{newStarterForm.emergency_contact_name}, {newStarterForm.emergency_contact_phone}</span></div>}
              {(() => {
                const isUK = employeeRaw?.primary_entity === 'Enko Capital Management LLP (UK)';
                if (isUK) {
                  return newStarterForm.rib_bank_name
                    ? <div style={p.field}>
                        <span style={p.fieldLabel}>Bank</span>
                        <span style={p.fieldValue}>{newStarterForm.rib_bank_name}{newStarterForm.rib_sort_code ? ` · ${newStarterForm.rib_sort_code}` : ''}</span>
                      </div>
                    : null;
                }
                return newStarterForm.rib_iban
                  ? <div style={p.field}>
                      <span style={p.fieldLabel}>IBAN</span>
                      <span style={p.fieldValue}>{newStarterForm.rib_iban}</span>
                    </div>
                  : null;
              })()}
            </div>
          )}
          {/* CLVIEW: the Section 2 (IT Setup) field grid - Microsoft 365, MFA, who completed it -
              does not render for a Country Lead. Their view carries nothing about IT setup, and
              these are IT setup FIELDS. The STATUS row above is deliberately kept (the s2 row with
              its fillSection cleared), so they can still see THAT IT setup is outstanding without
              seeing its contents or being offered the action. */}
          {/* HRS3: nor for hr. The server no longer sends hr these fields; the grid is also not
              rendered, so the IT setup content never appears on hr's screen. */}
          {!isPayrollDelegateView && newStarterForm?.section2_completed && role !== 'hr' && (
            <div style={p.dataGrid}>
              {newStarterForm.ms365_licence && <div style={p.field}><span style={p.fieldLabel}>Microsoft 365</span><span style={p.fieldValue}>{newStarterForm.ms365_licence}</span></div>}
              <div style={p.field}><span style={p.fieldLabel}>MFA</span><span style={p.fieldValue}>{newStarterForm.mfa_enrollment ? '✓ Enrolled' : '-'}</span></div>
              {newStarterForm.section2_completed_by && <div style={p.field}><span style={p.fieldLabel}>Completed by</span><span style={p.fieldValue}>{newStarterForm.section2_completed_by}</span></div>}
            </div>
          )}
          {newStarterForm?.section3_completed && (
            <div style={p.dataGrid}>
              {newStarterForm.salary && (
                <div style={p.field}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={p.fieldLabel}>Salary</span>
                    <button
                      onClick={() => setShowSalary(v => !v)}
                      title={showSalary ? 'Hide' : 'Show'}
                      style={{ background: 'none', border: 'none', padding: '1px', cursor: 'pointer', color: TEXT_MUTED, display: 'flex', alignItems: 'center', flexShrink: 0, lineHeight: 1 }}
                    >
                      {showSalary
                        ? <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
                        : <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
                      }
                    </button>
                  </div>
                  <span style={p.fieldValue}>
                    {showSalary
                      /* SALARYPERIOD: the currency prefix no longer falls back to a hardcoded
                         pound sign. That fallback rendered a Cameroon or South Africa salary
                         with a pound whenever currency was null, which is simply wrong -
                         currency is nullable and Section 3 is optional server-side, so this
                         happened for real records. With no currency we show the bare figure and
                         guess nothing. The period is appended when known, so the number is never
                         ambiguous between a monthly and an annual amount. */
                      ? [
                          newStarterForm.currency ? `${newStarterForm.currency} ` : '',
                          parseInt(newStarterForm.salary).toLocaleString(),
                          newStarterForm.salary_period === 'monthly' ? ' per month'
                            : newStarterForm.salary_period === 'annual' ? ' per annum' : '',
                        ].join('')
                      : '••••••'
                    }
                  </span>
                </div>
              )}
              <div style={p.field}><span style={p.fieldLabel}>Pension</span><span style={p.fieldValue}>{newStarterForm.pension_enrolled ? '✓ Enrolled' : '-'}</span></div>
              {/* HEALTHINS: display eligibility (the flag the portal keeps) instead of the removed provider field. */}
              <div style={p.field}><span style={p.fieldLabel}>Health Insurance</span><span style={p.fieldValue}>{newStarterForm.health_insurance_eligible ? '✓ Eligible' : '-'}</span></div>
            </div>
          )}
        </div>

        {/* ── Sponsor card ── (PAYPROFILEVIEW: hidden for the payroll+delegate view - visa-related) */}
        {sponsor_form ? (
          <div style={p.card}>
            <p style={p.sectionTitle}>Sponsor Form</p>
            <div style={p.statusGrid}>
              <div style={p.statusItem}>
                <span style={p.fieldLabel}>Status</span>
                <span style={{ ...p.fieldValue, color: sponsor_form.status === 'complete' ? '#2D4A3E' : GOLD }}>
                  {sponsor_form.status.replace('_', ' ').toUpperCase()}
                </span>
              </div>
              <div style={p.statusItem}><span style={p.fieldLabel}>Phase 1</span><span style={p.fieldValue}>{sponsor_form.phase1_completed ? '✓ Done' : 'Pending'}</span></div>
              <div style={p.statusItem}><span style={p.fieldLabel}>Phase 2</span><span style={p.fieldValue}>{sponsor_form.phase2_completed ? '✓ Done' : 'Pending'}</span></div>
              <div style={p.statusItem}><span style={p.fieldLabel}>Phase 3 (AO)</span><span style={p.fieldValue}>{recruitmentDone ? '✓ Done' : 'Pending'}</span></div>
            </div>
            {phase2Done && !recruitmentDone && (
              <div style={{ backgroundColor: '#FFF8EC', border: '0.5px solid rgba(201,168,76,0.3)', borderRadius: '8px', padding: '14px 18px', marginTop: '16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap' }}>
                <div>
                  <p style={{ fontSize: '13px', fontWeight: 600, color: NAVY_SHELL, margin: '0 0 3px 0' }}>Action required: Authorising Officer</p>
                  <p style={{ fontSize: '12px', color: TEXT_MUTED, margin: 0 }}>Please fill in the recruitment record.</p>
                </div>
                <button style={p.fillBtn} onClick={() => setShowRecruitment(true)}>Fill Recruitment Record</button>
              </div>
            )}
          </div>
        ) : (
          employee.requires_visa && (
            <div style={{ ...p.card, backgroundColor: '#FFF8EC', border: '0.5px solid rgba(201,168,76,0.3)' }}>
              <p style={{ fontSize: '13px', color: TEXT_MUTED, margin: 0 }}>Sponsor form not started: waiting for employee.</p>
            </div>
          )
        )}

        {/* ── Background Check card (COO - all employees) ── */}
        {/* HRPROFILE follow-up: hr has this card too; for a new joiner its upload opens Section 4B. */}
        {(role === 'coo' || role === 'hr') && (() => {
          const bgDocs = documents.filter(d => d.type === 'background_check' && !d.is_archived);
          return (
            <div
              ref={el => { cardRefs.current.background_check = el; }}
              style={{ ...p.card, ...(highlightCard === 'background_check' ? CARD_HIGHLIGHT : {}) }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                  <p style={{ ...p.sectionTitle, margin: 0 }}>Background Check</p>
                </div>
                <button
                  onClick={() => {
                    if (role === 'hr' && isNewJoiner) { setShowNsSection(4); return; }
                    setUploadDocType('background_check');
                    setShowUpload(true);
                  }}
                  style={{ fontSize: '12px', padding: '5px 12px', border: `1px solid ${GOLD}`, borderRadius: '6px', color: GOLD, background: 'transparent', cursor: 'pointer', whiteSpace: 'nowrap' }}
                >
                  Upload Background Check
                </button>
              </div>
              {/* S4BGNOTAVAIL: honest display when Compliance marked the check not available. Shown
                  as a distinct amber notice with the reason, never as a completed/uploaded check. */}
              {newStarterForm?.background_check_not_available && (
                <div style={{ backgroundColor: '#FFF8EC', border: '1px solid rgba(201,168,76,0.4)', borderRadius: '8px', padding: '10px 14px', marginBottom: '12px' }}>
                  <p style={{ margin: 0, fontSize: '12px', fontWeight: 600, color: '#7a4d00' }}>No background check available</p>
                  {newStarterForm.background_check_not_available_reason && (
                    <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: TEXT_MUTED }}>
                      Reason: {newStarterForm.background_check_not_available_reason}
                    </p>
                  )}
                </div>
              )}
              {bgDocs.length === 0 ? (
                <p style={p.empty}>{newStarterForm?.background_check_not_available ? 'No background check on file (marked not available above).' : 'No background check uploaded yet.'}</p>
              ) : (
                <ScrollableTable><table style={p.table}>
                  <thead>
                    <tr>
                      <th style={p.th}>File</th>
                      <th style={p.th}>Uploaded</th>
                      <th style={p.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bgDocs.map(doc => (
                      <tr key={doc.id} style={{ borderBottom: '0.5px solid rgba(26,39,68,0.06)' }}>
                        <td style={p.td}>
                          <button onClick={() => openDocument(doc.id)} disabled={docLoading === doc.id} style={p.docLink}>
                            {docLoading === doc.id ? 'Opening…' : doc.file_name}
                          </button>
                          {/* DLNAME: download under the readable server name, beside the preview. */}
                          <button onClick={() => downloadDocument(doc.id)} style={p.docLink}>Download</button>
                        </td>
                        <td style={p.td}>{toDisplayDate(doc.uploaded_at).toLocaleDateString()}</td>
                        <td style={p.td}>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            <button
                              onClick={() => openDocument(doc.id)}
                              disabled={docLoading === doc.id}
                              style={{ fontSize: '11px', padding: '3px 8px', border: '0.5px solid rgba(26,39,68,0.2)', borderRadius: '4px', background: 'transparent', cursor: 'pointer', color: NAVY_SHELL }}
                            >
                              View
                            </button>
                              {/* DLNAME: download under the readable server name, beside the preview. */}
                              <button onClick={() => downloadDocument(doc.id)} style={p.docLink}>Download</button>
                            {canArchiveDocument(doc) ? (
                              /* ARCHIVEGATE: same endpoint, same rules - background_check is not
                                 in MANUAL_ARCHIVE_TYPES (and SELFARCHCONTRACT did not add it:
                                 only self-uploaded types were widened), so this branch stays
                                 coo/admin only in practice. */
                              <button
                                onClick={() => handleArchiveDocument(doc.id)}
                                disabled={archivingDoc === doc.id}
                                style={{ fontSize: '11px', padding: '3px 8px', border: '0.5px solid rgba(166,58,58,0.3)', borderRadius: '4px', background: 'transparent', cursor: archivingDoc === doc.id ? 'wait' : 'pointer', color: '#A63A3A' }}
                              >
                                {archivingDoc === doc.id ? '...' : 'Archive'}
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table></ScrollableTable>
              )}
            </div>
          );
        })()}

        {/* ── Documents card ── (CLPROFILE: now VISIBLE to a Country Lead for their own entity,
             matching the COO view. The backend re-checks the entity scope on every documents
             endpoint, so this is a display change on top of a server-enforced widening, never
             instead of one.) */}
        {(
        <div style={p.card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
            <p style={{ ...p.sectionTitle, margin: 0 }}>Documents ({visibleDocuments.filter(d => !d.is_archived).length})</p>
            {/* CLPROFILE: upload is offered to the COO as before, and now to a Country Lead for
                their own entity. ARCHIVE is deliberately NOT widened - see canArchiveDocument
                above, which still excludes payroll, mirroring the server-side decision that
                uploading and archiving are different powers. */}
            {(role === 'coo' || role === 'hr' || isPayrollDelegateView) && (
              <button
                onClick={() => { setUploadDocType(''); setShowUpload(true); }}
                style={{ fontSize: '12px', padding: '5px 12px', border: `1px solid ${GOLD}`, borderRadius: '6px', color: GOLD, background: 'transparent', cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                + Upload Document
              </button>
            )}
          </div>
          {/* CLVIEW: visibleDocuments, so an internal-only row is not counted, not listed and
              not rendered for a Country Lead. Identical to `documents` for every other role. */}
          {visibleDocuments.filter(d => !d.is_archived).length === 0 ? (
            <p style={p.empty}>No documents uploaded yet.</p>
          ) : (
            <ScrollableTable><table style={p.table}>
              <thead>
                <tr>
                  <th style={p.th}>Type</th>
                  <th style={p.th}>File</th>
                  <th style={p.th}>Expiry / Closed</th>
                  <th style={p.th}>Uploaded</th>
                  <th style={p.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {visibleDocuments.filter(d => !d.is_archived).map(doc => {
                  const expDate = doc.expiry_date ? new Date(doc.expiry_date) : null;
                  const daysLeft = expDate ? Math.floor((expDate - new Date()) / (1000 * 60 * 60 * 24)) : null;
                  const expiryColor = daysLeft !== null && daysLeft <= 90 ? '#A63A3A' : NAVY_SHELL;
                  const dateLabel = doc.expiry_date
                    ? (doc.type === 'job_advertisement'
                        ? `Closed ${toDisplayDate(doc.expiry_date).toLocaleDateString()}`
                        : toDisplayDate(doc.expiry_date).toLocaleDateString())
                    : '-';
                  return (
                    <tr key={doc.id} style={{ borderBottom: '0.5px solid rgba(26,39,68,0.06)' }}>
                      {/* DOCLABELCASE: shared label helper, so cv renders "CV" not "Cv". */}
                      <td style={p.td}>{documentTypeLabel(doc.type)}</td>
                      <td style={p.td}>
                        <button
                          onClick={() => openDocument(doc.id)}
                          disabled={docLoading === doc.id}
                          style={p.docLink}
                        >
                          {docLoading === doc.id ? 'Opening…' : doc.file_name}
                        </button>
                          {/* DLNAME: download under the readable server name, beside the preview. */}
                          <button onClick={() => downloadDocument(doc.id)} style={p.docLink}>Download</button>
                      </td>
                      <td style={{ ...p.td, color: expiryColor }}>{dateLabel}</td>
                      <td style={p.td}>{toDisplayDate(doc.uploaded_at).toLocaleDateString()}</td>
                      <td style={p.td}>
                        <div style={{ display: 'flex', gap: '6px' }}>
                          <button
                            onClick={() => openDocument(doc.id)}
                            disabled={docLoading === doc.id}
                            style={{ fontSize: '11px', padding: '3px 8px', border: '0.5px solid rgba(26,39,68,0.2)', borderRadius: '4px', background: 'transparent', cursor: 'pointer', color: NAVY_SHELL }}
                          >
                            View
                          </button>
                            {/* DLNAME: download under the readable server name, beside the preview. */}
                            <button onClick={() => downloadDocument(doc.id)} style={p.docLink}>Download</button>
                          {/* CATFIX: coo/admin/hr can categorize a stray 'uncategorized' doc here.
                              CATGATE: this comment always named coo/admin/hr, but the control had
                              no role check at all - matches the endpoint's own
                              require_self_or_roles(employee_id, current_user, "coo", "admin",
                              "hr") exactly, so a Country Lead (role payroll) no longer sees a
                              control that always 403'd. Neither mount site of this component
                              passes role="admin" or role="hr" today (Dashboard.js is always coo,
                              App.js's Country Lead mount is always payroll), so this only ever
                              actually admits coo in the running app right now - included fully
                              here regardless, so the display stays correct if that ever changes. */}
                          {doc.type === 'uncategorized' && (role === 'coo' || role === 'admin' || role === 'hr') && (
                            <>
                              <Labeled label={dashT('profileFields.document_category')} dark={isDarkMode}><select
                                value={categorizeType[doc.id] || ''}
                                onChange={e => setCategorizeType(prev => ({ ...prev, [doc.id]: e.target.value }))}
                                disabled={categorizingDoc === doc.id}
                                style={{ fontSize: '11px', padding: '3px 6px', borderRadius: '4px', border: '0.5px solid rgba(26,39,68,0.2)', background: 'transparent', color: NAVY_SHELL }}
                              >
                                <option value="">Select category…</option>
                                {DOC_CATEGORIES.map(cat => (
                                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                                ))}
                              </select></Labeled>
                              {/* CATFIX2: same required metadata as MigrationConfirmation Step A */}
                              {DATE_EXPIRY_TYPES.includes(categorizeType[doc.id]) && (
                                <Labeled label={dashT('profileFields.document_expiry_date')} dark={isDarkMode}><input
                                  type="date"
                                  title="Expiry date (required)"
                                  value={categorizeMeta[doc.id]?.expiry_date || ''}
                                  onChange={e => setMeta(doc.id, 'expiry_date', e.target.value)}
                                  disabled={categorizingDoc === doc.id}
                                  style={{ fontSize: '11px', padding: '3px 6px', borderRadius: '4px', border: '0.5px solid rgba(26,39,68,0.2)', background: 'transparent', color: NAVY_SHELL }}
                                /></Labeled>
                              )}
                              {categorizeType[doc.id] === 'passport' && (
                                <>
                                  <Labeled label={dashT('profileFields.passport_number')} dark={isDarkMode}><input
                                    type="text"
                                    placeholder="Passport number *"
                                    value={categorizeMeta[doc.id]?.passport_number || ''}
                                    onChange={e => setMeta(doc.id, 'passport_number', e.target.value)}
                                    disabled={categorizingDoc === doc.id}
                                    style={{ fontSize: '11px', padding: '3px 6px', borderRadius: '4px', border: '0.5px solid rgba(26,39,68,0.2)', background: 'transparent', color: NAVY_SHELL, width: '110px' }}
                                  /></Labeled>
                                  <Labeled label={dashT('profileFields.passport_country')} dark={isDarkMode}><input
                                    type="text"
                                    placeholder="Passport country *"
                                    value={categorizeMeta[doc.id]?.passport_country || ''}
                                    onChange={e => setMeta(doc.id, 'passport_country', e.target.value)}
                                    disabled={categorizingDoc === doc.id}
                                    style={{ fontSize: '11px', padding: '3px 6px', borderRadius: '4px', border: '0.5px solid rgba(26,39,68,0.2)', background: 'transparent', color: NAVY_SHELL, width: '110px' }}
                                  /></Labeled>
                                </>
                              )}
                              <button
                                onClick={() => categorizeDoc(doc.id)}
                                disabled={categorizingDoc === doc.id || !categorizeReady(doc.id)}
                                style={{ fontSize: '11px', padding: '3px 8px', border: `0.5px solid ${GOLD}`, borderRadius: '4px', background: 'transparent', cursor: (categorizingDoc === doc.id || !categorizeReady(doc.id)) ? 'default' : 'pointer', color: GOLD, opacity: (categorizingDoc === doc.id || !categorizeReady(doc.id)) ? 0.5 : 1 }}
                              >
                                {categorizingDoc === doc.id ? '...' : 'Categorize'}
                              </button>
                            </>
                          )}
                          {/* ARCHIVEGATE: was `role === 'coo'`, which wrongly excluded admin -
                              the endpoint allows coo AND admin unrestricted, plus the document's
                              own employee for MANUAL_ARCHIVE_TYPES. */}
                          {canArchiveDocument(doc) && (
                            <button
                              onClick={() => handleArchiveDocument(doc.id)}
                              disabled={archivingDoc === doc.id}
                              style={{ fontSize: '11px', padding: '3px 8px', border: '0.5px solid rgba(166,58,58,0.3)', borderRadius: '4px', background: 'transparent', cursor: archivingDoc === doc.id ? 'wait' : 'pointer', color: '#A63A3A' }}
                            >
                              {archivingDoc === doc.id ? '...' : 'Archive'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table></ScrollableTable>
          )}
        </div>
        )}

        {/* ── Signature & Photo card ── (CLPROFILE: now VISIBLE to a Country Lead, matching the
             COO view. Read-only here; photo/signature are NOT in the Country Lead upload
             whitelist, because they are written by the New Starter Part A self-service path and
             archived automatically - a manual upload would bypass that supersede logic.) */}
        {(
        <div style={p.card}>
          <p style={p.sectionTitle}>Signature & Photo</p>
          <p style={{ fontSize: '11px', color: TEXT_MUTED, margin: '0 0 16px 0' }}>
            Uploaded by the employee in Section 1 of the New Starter form.
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: gridCols(isNarrow, '1fr 1fr'), gap: '16px' }}>
            <div>
              <p style={p.fieldLabel}>Signature sample</p>
              {employeeRaw?.signature_url
                ? (
                  // DLNAME: View and Download as a pair, wrapped in a fragment because the
                  // branch they sit in takes ONE expression.
                  <>
                    <button style={p.viewFileBtn} onClick={() => viewProfileFile('signature')}>View current signature</button>
                    {/* DLNAME: download under the readable server name. */}
                    <button style={p.viewFileBtn} onClick={() => downloadProfileFile('signature')}>Download</button>
                  </>
                )
                : <p style={{ fontSize: '13px', color: TEXT_MUTED, fontStyle: 'italic', margin: '6px 0 0 0' }}>Not uploaded yet</p>
              }
            </div>
            <div>
              <p style={p.fieldLabel}>Photo</p>
              {employeeRaw?.photo_url
                ? (
                  // DLNAME: View and Download as a pair, wrapped in a fragment because the
                  // branch they sit in takes ONE expression.
                  <>
                    <button style={p.viewFileBtn} onClick={() => viewProfileFile('photo')}>View current photo</button>
                    {/* DLNAME: download under the readable server name. */}
                    <button style={p.viewFileBtn} onClick={() => downloadProfileFile('photo')}>Download</button>
                  </>
                )
                : <p style={{ fontSize: '13px', color: TEXT_MUTED, fontStyle: 'italic', margin: '6px 0 0 0' }}>Not uploaded yet</p>
              }
            </div>
          </div>
        </div>
        )}

        {/* ── Tasks card ── */}
        {/* TASKHIDEDONE: pending/overdue only by default; completed tasks live behind the
            "Show completed" link (kept in the DB untouched, purely a display choice). */}
        <div style={p.card}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <p style={p.sectionTitle}>Tasks ({openTasks.length})</p>
            {completedTasks.length > 0 && (
              <button
                onClick={() => setShowCompletedTasks(v => !v)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '11px', color: TEXT_MUTED, textDecoration: 'underline', padding: 0 }}
                title="Completed tasks are kept for audit; they are only hidden from this default view"
              >
                {showCompletedTasks ? 'Hide completed' : `Show completed (${completedTasks.length})`}
              </button>
            )}
          </div>
          {openTasks.length === 0 && <p style={p.empty}>No open tasks</p>}
          {/* TASKACTOR: grouped by WHO MUST ACT, the employee's own tasks first. The heading
              carries the "To do by" label once per group rather than repeating it on every row,
              which keeps the row itself as short as it was. */}
          {groupedOpenTasks.map(({ actor, items }) => (
          <div key={actor}>
            <p style={{ margin: '12px 0 2px', fontSize: '11px', fontWeight: 600,
                        color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
              {dashT('tasks.toDoBy')}: {actorLabel(actor)}
            </p>
          {items.map(task => {
            const dotColor = isTaskOverdue(task) ? OVERDUE_RED : GOLD;
            // P45COO: same exact UK-entity check used elsewhere in this file (line ~1997) and by
            // EmployeeView's isUK / the backend's _is_uk_linked. The waiver only makes sense for a
            // UK employee, and the endpoint is coo/admin-gated, so only show it to coo/admin.
            const showNoP45 = task.task_type === 'upload_p45'
              && employeeRaw?.primary_entity === 'Enko Capital Management LLP (UK)'
              && ['coo', 'admin', 'hr'].includes(role);
            return (
              <div key={task.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 0', borderBottom: '0.5px solid rgba(26,39,68,0.06)' }}>
                <div style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: dotColor, flexShrink: 0 }} />
                {(role === 'coo' || role === 'hr') && HR_UPLOAD_TASK_TYPES.has(task.task_type) ? (
                  <button type="button" onClick={() => openUploadForTask(task.task_type)}
                    data-testid={`open-upload-${task.task_type}`}
                    style={{ flex: 1, fontSize: '13px', color: NAVY_SHELL, background: 'none', border: 'none',
                             padding: 0, textAlign: 'left', cursor: 'pointer', textDecoration: 'underline' }}>
                    {task.title}
                  </button>
                ) : (
                  <span style={{ flex: 1, fontSize: '13px', color: NAVY_SHELL }}>{task.title}</span>
                )}
                {showNoP45 && (
                  <button
                    onClick={handleMarkP45NotApplicable}
                    disabled={p45Saving}
                    title="A P45 is issued by a previous UK employer - use this if this employee was never issued one. No Starter Declaration is collected here."
                    style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '6px', border: `1px solid ${GOLD}`, background: 'transparent', color: NAVY_SHELL, cursor: p45Saving ? 'wait' : 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
                  >
                    {p45Saving ? 'Saving…' : "Don't have P45"}
                  </button>
                )}
                {isTaskOverdue(task) && <OverdueLabel />}
                <span style={{ fontSize: '11px', color: TEXT_MUTED }}>
                  {task.due_date ? toDisplayDate(task.due_date).toLocaleDateString() : '-'}
                </span>
              </div>
            );
          })}
          </div>
          ))}
          {showCompletedTasks && completedTasks.map(task => (
            <div key={task.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 0', borderBottom: '0.5px solid rgba(26,39,68,0.06)', opacity: 0.65 }}>
              <div style={{ width: '7px', height: '7px', borderRadius: '50%', backgroundColor: '#2D4A3E', flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: '13px', color: NAVY_SHELL }}>{task.title}</span>
              <span style={{ fontSize: '11px', color: TEXT_MUTED }}>
                {task.due_date ? toDisplayDate(task.due_date).toLocaleDateString() : '-'}
              </span>
            </div>
          ))}
        </div>

        {/* ── Reminders card ── */}
        <div style={p.card}>
          <p style={p.sectionTitle}>Reminders ({upcomingReminders.length})</p>
          {upcomingReminders.length === 0 ? <p style={p.empty}>No reminders</p> : (
            <ScrollableTable><table style={p.table}>
              <thead>
                <tr>
                  <th style={p.th}>Reminder</th>
                  <th style={p.th}>Due date</th>
                  <th style={p.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {upcomingReminders.map((reminder, index) => {
                  const isOverdue = !reminder.acknowledged && reminder.due_date && new Date(reminder.due_date) < new Date();
                  return (
                    <tr key={index} style={{ borderBottom: '0.5px solid rgba(26,39,68,0.06)' }}>
                      <td style={p.td}>{reminder.type.replace(/_/g, ' ').toUpperCase()} REMINDER</td>
                      <td style={p.td}>{reminder.due_date ? toDisplayDate(reminder.due_date).toLocaleDateString() : '-'}</td>
                      <td style={p.td}>
                        {reminder.acknowledged
                          ? <span style={{ ...p.badge, backgroundColor: '#E5E7EB', color: '#374151', border: '1px solid #D1D5DB' }}>Done</span>
                          : isOverdue
                            ? <span style={{ ...p.badge, backgroundColor: '#FDF0F0', color: '#A63A3A' }}>Overdue</span>
                            : <span style={{ ...p.badge, backgroundColor: '#FFF8EC', color: '#7a4d00' }}>Pending</span>
                        }
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table></ScrollableTable>
          )}
        </div>

      </div>

      {showUpload && (
        <DocumentUpload
          employeeId={employeeId}
          employeeName={data.employee.name}
          initialDocumentType={uploadDocType}
          allowedTypeKeys={isPayrollDelegateView ? COUNTRY_LEAD_UPLOAD_TYPES : null}
          onClose={() => { setShowUpload(false); setUploadDocType(''); }}
          onUploaded={() => loadData()}
        />
      )}

      {/* CONVERTFORM: the intern-to-permanent conversion form. Same overlay pattern as the
          document-list modal below (fixed rgba backdrop, centred card, dark/light aware). Collects
          the end date (required), job title and target type, then submitConvertForm chains the
          existing endpoints. Does not change what the conversion itself does. */}
      {showConvertForm && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}
          onClick={() => { if (!converting) setShowConvertForm(false); }}>
          <div onClick={e => e.stopPropagation()}
            style={{ backgroundColor: isDarkMode ? NAVY_SHELL : '#fff', border: isDarkMode ? '1px solid rgba(201,168,76,0.15)' : 'none', borderRadius: '12px', padding: '28px', width: '460px', maxWidth: '90vw', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
            <h2 style={{ fontSize: '16px', fontWeight: 600, color: isDarkMode ? TEXT_DARK_MODE : NAVY_FORM, margin: '0 0 4px 0' }}>
              Convert to permanent employee
            </h2>
            <p style={{ fontSize: '13px', color: isDarkMode ? TEXT_MUTED : '#666', margin: '0 0 20px 0' }}>
              {employee?.name}. This clears the internship end date, cancels any pending offboarding
              task, and creates a background check task for Compliance. Login access and visa
              sponsorship data are not changed.
            </p>

            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: isDarkMode ? TEXT_DARK_MODE : NAVY_FORM, marginBottom: '4px' }} htmlFor={`${a11yId}-internship-end-date`}>
              Internship end date <span style={{ color: '#c0392b' }}>*</span>
            </label>
            
            <input id={`${a11yId}-internship-end-date`}
              type="date"
              value={convertForm.internship_end_date}
              onChange={e => setConvertForm({ ...convertForm, internship_end_date: e.target.value })}
              style={{ width: '100%', padding: '8px 10px', fontSize: '13px', borderRadius: '6px', border: `1px solid ${isDarkMode ? 'rgba(201,168,76,0.25)' : '#ccc'}`, backgroundColor: isDarkMode ? '#243058' : '#fff', color: isDarkMode ? TEXT_DARK_MODE : NAVY_SHELL, marginBottom: '16px', boxSizing: 'border-box' }}
            />

            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: isDarkMode ? TEXT_DARK_MODE : NAVY_FORM, marginBottom: '4px' }} htmlFor={`${a11yId}-job-title`}>
              Job title
            </label>
            
            <input id={`${a11yId}-job-title`}
              type="text"
              value={convertForm.job_title}
              placeholder="e.g. Analyst"
              onChange={e => setConvertForm({ ...convertForm, job_title: e.target.value })}
              style={{ width: '100%', padding: '8px 10px', fontSize: '13px', borderRadius: '6px', border: `1px solid ${isDarkMode ? 'rgba(201,168,76,0.25)' : '#ccc'}`, backgroundColor: isDarkMode ? '#243058' : '#fff', color: isDarkMode ? TEXT_DARK_MODE : NAVY_SHELL, marginBottom: '16px', boxSizing: 'border-box' }}
            />

            <label style={{ display: 'block', fontSize: '13px', fontWeight: 500, color: isDarkMode ? TEXT_DARK_MODE : NAVY_FORM, marginBottom: '4px' }}>
              Convert to
            </label>
            {/* The two valid non-intern targets in this codebase (AddEmployee's own type set is
                employee / contractor / intern). Default 'employee', matching AddEmployee. */}
            <select
              value={convertForm.employee_type}
              onChange={e => setConvertForm({ ...convertForm, employee_type: e.target.value })}
              style={{ width: '100%', padding: '8px 10px', fontSize: '13px', borderRadius: '6px', border: `1px solid ${isDarkMode ? 'rgba(201,168,76,0.25)' : '#ccc'}`, backgroundColor: isDarkMode ? '#243058' : '#fff', color: isDarkMode ? TEXT_DARK_MODE : NAVY_SHELL, marginBottom: '16px', boxSizing: 'border-box' }}
            >
              <option value="employee">Permanent employee</option>
              <option value="contractor">Contractor</option>
            </select>

            {convertError && (
              <p style={{ fontSize: '12px', color: '#c0392b', margin: '0 0 12px 0' }}>{convertError}</p>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '4px' }}>
              <button
                type="button"
                onClick={() => setShowConvertForm(false)}
                disabled={converting}
                style={{ fontSize: '13px', padding: '8px 14px', borderRadius: '6px', border: `1px solid ${isDarkMode ? 'rgba(201,168,76,0.25)' : '#ccc'}`, background: 'none', color: isDarkMode ? TEXT_MUTED : '#666', cursor: converting ? 'default' : 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitConvertForm}
                disabled={converting}
                style={{ fontSize: '13px', padding: '8px 14px', borderRadius: '6px', border: 'none', backgroundColor: '#2D4A3E', color: '#fff', fontWeight: 600, cursor: converting ? 'default' : 'pointer' }}
              >
                {converting ? 'Converting...' : 'Convert'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showDocList && (() => {
        const listDocs = documents.filter(d => d.type === docListType && !d.is_archived);
        const typeLabel = documentTypeLabel(docListType);  // DOCLABELCASE
        return (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
            {/* DARKFIX: the document-list modal was entirely hardcoded light (white card, dark
                heading, near-white row backgrounds), so it opened as a bright slab over a dark
                page. Card and text now follow the same dark/light pair ui.js's ConfirmModal
                uses, and the rows reuse mkP's own `dataGrid` surface values. */}
            <div style={{ backgroundColor: isDarkMode ? NAVY_SHELL : '#fff', border: isDarkMode ? '1px solid rgba(201,168,76,0.15)' : 'none', borderRadius: '12px', padding: '28px', width: '540px', maxWidth: '90vw', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                <div>
                  <h2 style={{ fontSize: '16px', fontWeight: 600, color: isDarkMode ? TEXT_DARK_MODE : NAVY_FORM, margin: 0 }}>{typeLabel}</h2>
                  <p style={{ fontSize: '13px', color: isDarkMode ? TEXT_MUTED : '#666', margin: '3px 0 0 0' }}>{listDocs.length} active file{listDocs.length !== 1 ? 's' : ''}</p>
                </div>
                <button onClick={() => setShowDocList(false)} style={{ background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: isDarkMode ? TEXT_MUTED : '#666' }}>✕</button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                {listDocs.map(doc => (
                  <div key={doc.id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 12px', backgroundColor: isDarkMode ? '#243058' : '#FAFAF8', borderRadius: '6px', border: `0.5px solid ${isDarkMode ? 'rgba(201,168,76,0.1)' : 'rgba(26,39,68,0.08)'}` }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ fontSize: '13px', color: isDarkMode ? TEXT_DARK_MODE : NAVY_SHELL, fontWeight: 500, margin: '0 0 2px 0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.file_name}</p>
                      <p style={{ fontSize: '11px', color: TEXT_MUTED, margin: 0 }}>
                        Uploaded {toDisplayDate(doc.uploaded_at).toLocaleDateString()}
                        {doc.expiry_date && ` · ${docListType === 'job_advertisement' ? 'Closed' : 'Expires'} ${toDisplayDate(doc.expiry_date).toLocaleDateString()}`}
                      </p>
                    </div>
                    <button
                      onClick={() => openDocument(doc.id)}
                      disabled={docLoading === doc.id}
                      style={{ fontSize: '12px', padding: '5px 10px', border: `0.5px solid ${isDarkMode ? 'rgba(201,168,76,0.25)' : 'rgba(26,39,68,0.2)'}`, borderRadius: '5px', background: 'transparent', cursor: 'pointer', color: isDarkMode ? GOLD : NAVY_SHELL, whiteSpace: 'nowrap' }}
                    >
                      {docLoading === doc.id ? '...' : 'View'}
                    </button>
                      {/* DLNAME: download under the readable server name, beside the preview. */}
                      <button onClick={() => downloadDocument(doc.id)} style={p.docLink}>Download</button>
                    {/* ARCHIVEGATE: this button previously had NO gate at all, so a viewer the
                        endpoint would 403 was still offered it. */}
                    {canArchiveDocument(doc) && (
                      <button
                        onClick={() => handleArchiveDocument(doc.id)}
                        disabled={archivingDoc === doc.id}
                        style={{ fontSize: '12px', padding: '5px 10px', border: '0.5px solid rgba(166,58,58,0.3)', borderRadius: '5px', background: 'transparent', cursor: archivingDoc === doc.id ? 'wait' : 'pointer', color: '#A63A3A', whiteSpace: 'nowrap' }}
                      >
                        {archivingDoc === doc.id ? '...' : 'Archive'}
                      </button>
                    )}
                  </div>
                ))}
              </div>
              {/* HRPROFILE: this list opens from the visa checklist's View, whose uploads stay the COO's. */}
              {role !== 'hr' && (
              <div style={{ marginTop: '20px', display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => { setUploadDocType(docListType); setShowDocList(false); setShowUpload(true); }}
                  style={{ fontSize: '12px', padding: '7px 14px', border: `1px solid ${GOLD}`, borderRadius: '6px', color: GOLD, background: 'transparent', cursor: 'pointer' }}
                >
                  + Upload new version
                </button>
              </div>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
}

const mkP = (dark) => ({
  loading:      { display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', fontSize: '16px', color: TEXT_MUTED, fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' },
  card:         { backgroundColor: dark ? NAVY_SHELL : '#fff', border: `0.5px solid ${dark ? 'rgba(201,168,76,0.15)' : 'rgba(26,39,68,0.08)'}`, borderRadius: '8px', padding: '20px', marginBottom: '12px' },
  sectionTitle: { fontSize: '9px', fontWeight: 700, color: dark ? TEXT_MUTED : NAVY_SHELL, letterSpacing: '0.08em', textTransform: 'uppercase', opacity: 0.5, margin: '0 0 12px 0' },
  avatar:       { width: '48px', height: '48px', borderRadius: '50%', backgroundColor: NAVY_SHELL, color: GOLD, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '20px', fontWeight: '700', flexShrink: 0 },
  name:         { fontSize: '18px', fontWeight: 600, color: dark ? TEXT_DARK_MODE : NAVY_SHELL, margin: '0 0 4px 0' },
  roleText:     { fontSize: '13px', color: TEXT_MUTED, margin: 0 },
  visaBadge:    { display: 'inline-block', backgroundColor: GOLD, color: '#fff', fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px' },
  leaverBtn:    { backgroundColor: '#A63A3A', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 16px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' },
  archiveBtn:   { backgroundColor: NAVY_SHELL, color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 16px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' },
  unarchiveBtn: { backgroundColor: '#2D4A3E', color: '#fff', border: 'none', borderRadius: '6px', padding: '8px 16px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap' },
  detailsGrid:  { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px' },
  field:        { display: 'flex', flexDirection: 'column', gap: '3px' },
  fieldLabel:   { fontSize: '10px', color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.06em', margin: 0 },
  fieldValue:   { fontSize: '13px', color: dark ? TEXT_DARK_MODE : NAVY_SHELL, fontWeight: 500, margin: 0 },
  statusGrid:   { display: 'flex', gap: '24px', flexWrap: 'wrap', marginBottom: '4px' },
  statusItem:   { display: 'flex', flexDirection: 'column', gap: '3px' },
  dataGrid:     { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px', marginTop: '16px', padding: '14px 16px', backgroundColor: dark ? '#243058' : '#FAFAF8', borderRadius: '6px', border: `0.5px solid ${dark ? 'rgba(201,168,76,0.1)' : 'rgba(26,39,68,0.06)'}` },
  fillBtn:      { backgroundColor: NAVY_SHELL, color: '#fff', border: 'none', borderRadius: '6px', padding: '6px 14px', fontSize: '12px', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 },
  table:        { width: '100%', borderCollapse: 'collapse' },
  th:           { textAlign: 'left', padding: '10px 0', fontSize: '10px', color: TEXT_MUTED, textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: `0.5px solid ${dark ? 'rgba(201,168,76,0.1)' : 'rgba(26,39,68,0.06)'}`, fontWeight: 600 },
  td:           { padding: '10px 0', fontSize: '13px', color: dark ? TEXT_DARK_MODE : NAVY_SHELL },
  docLink:      { background: 'none', border: 'none', color: GOLD, fontSize: '13px', cursor: 'pointer', padding: 0, textDecoration: 'none' },
  badge:        { padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600 },
  viewFileBtn:  { display: 'inline-block', padding: '6px 12px', fontSize: '12px', color: dark ? TEXT_DARK_MODE : NAVY_SHELL, background: 'transparent', border: `0.5px solid ${dark ? 'rgba(201,168,76,0.2)' : 'rgba(26,39,68,0.2)'}`, borderRadius: '6px', cursor: 'pointer', marginTop: '6px' },
  empty:        { fontSize: '13px', color: TEXT_MUTED, fontStyle: 'italic', margin: '4px 0' },
});

export default EmployeeProfile;
